import React, { useEffect, useState, useCallback } from 'react';
import {
  Box, VStack, HStack, Heading, Text, Table, Thead, Tbody, Tr, Th, Td,
  Badge, Select, Button, ButtonGroup, useDisclosure, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalCloseButton, ModalFooter, Accordion,
  AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, FormControl, FormLabel,
  Input, Textarea, NumberInput, NumberInputField, useToast, Spinner,
  IconButton, Tooltip, SimpleGrid, Checkbox, Alert, AlertIcon,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, ChevronDownIcon, ChevronUpIcon, ViewIcon, CopyIcon, StarIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';
import { riskApi, departmentApi, lookupApi, masterRiskApi, API_BASE_URL } from '../services/api';
import { Risk, Department, RiskFull, FiscalYear, MasterRisk } from '../types';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import EvidenceUpload from '../components/EvidenceUpload';
import { getRiskLevelStyle, getRiskLevelLabel } from '../utils/riskLevel';

const RiskRegisterPage: React.FC = () => {
  const { isAdmin, isManager, isGuest, user } = useAuth();
  const { t } = useLanguage();
  const [risks, setRisks] = useState<Risk[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [fiscalYears, setFiscalYears] = useState<FiscalYear[]>([]);
  const [riskTypes, setRiskTypes] = useState<any[]>([]);
  const [strategies, setStrategies] = useState<any[]>([]);
  const [riskMgmtTypes, setRiskMgmtTypes] = useState<any[]>([]);
  const [selectedUnitId, setSelectedUnitId] = useState('');
  const [selectedFiscalYear, setSelectedFiscalYear] = useState('');
  const [selectedErmLevel, setSelectedErmLevel] = useState('');
  const [loading, setLoading] = useState(true);
  const toast = useToast();
  const navigate = useNavigate();

  // Master risk view
  const [viewMode, setViewMode] = useState<'master' | 'all'>('master');
  const [masterRisks, setMasterRisks] = useState<MasterRisk[]>([]);
  const [expandedMasterId, setExpandedMasterId] = useState<string | null>(null);
  const [masterYearRisks, setMasterYearRisks] = useState<Risk[]>([]);
  const [loadingMaster, setLoadingMaster] = useState(false);

  // Expanded risk tracking (risk_id -> full detail)
  const [expandedRisks, setExpandedRisks] = useState<Record<string, RiskFull>>({});
  const [loadingRisk, setLoadingRisk] = useState<string | null>(null);

  // Add sub-entity modals
  const { isOpen: isAddCauseOpen, onOpen: onAddCauseOpen, onClose: onAddCauseClose } = useDisclosure();
  const { isOpen: isAddICOpen, onOpen: onAddICOpen, onClose: onAddICClose } = useDisclosure();
  const { isOpen: isAddACOpen, onOpen: onAddACOpen, onClose: onAddACClose } = useDisclosure();
  const { isOpen: isAddKRIOpen, onOpen: onAddKRIOpen, onClose: onAddKRIClose } = useDisclosure();

  // Edit sub-entity modals
  const { isOpen: isEditCauseOpen, onOpen: onEditCauseOpen, onClose: onEditCauseClose } = useDisclosure();
  const { isOpen: isEditICOpen, onOpen: onEditICOpen, onClose: onEditICClose } = useDisclosure();
  const { isOpen: isEditACOpen, onOpen: onEditACOpen, onClose: onEditACClose } = useDisclosure();
  const { isOpen: isEditKRIOpen, onOpen: onEditKRIOpen, onClose: onEditKRIClose } = useDisclosure();

  const [addCauseData, setAddCauseData] = useState({ cause_id: '', cause_number: 1, cause_description: '' });
  const [addICData, setAddICData] = useState({ ic_id: '', ic_no: 1, control_description: '', ic_owner: '', control_type_id: 1, evidence_link: '', evidence_name: '' });
  const [addACData, setAddACData] = useState({ ac_id: '', ac_no: 1, control_description: '', ac_owner: '', control_type_id: 1, evidence_link: '', evidence_name: '' });
  const [addKRIData, setAddKRIData] = useState({ kri_id: '', kri_name: '', target_value: '', unit: '', kri_owner: '' });
  const [activeRiskId, setActiveRiskId] = useState('');
  const [activeFiscalYearId, setActiveFiscalYearId] = useState<number | undefined>(undefined);
  const [controlTypes, setControlTypes] = useState<any[]>([]);

  // Edit data state
  const [editCauseData, setEditCauseData] = useState({ cause_id: '', cause_number: 1, cause_description: '' });
  const [editICData, setEditICData] = useState({ ic_id: '', ic_no: 1, control_description: '', ic_owner: '', control_type_id: 1, evidence_link: '', evidence_name: '' });
  const [editACData, setEditACData] = useState({ ac_id: '', ac_no: 1, control_description: '', ac_owner: '', control_type_id: 1, evidence_link: '', evidence_name: '' });
  const [editKRIData, setEditKRIData] = useState({ kri_id: '', kri_name: '', target_value: '', unit: '', kri_owner: '' });
  const [editingCauseId, setEditingCauseId] = useState<number>(0);
  const [editingICId, setEditingICId] = useState<number>(0);
  const [editingACId, setEditingACId] = useState<number>(0);
  const [editingKRIId, setEditingKRIId] = useState<number>(0);

  // Copy-to-next-year modal (no year selection: next year only)
  const [copyTarget, setCopyTarget] = useState<Risk | null>(null);
  const [copyYearId, setCopyYearId] = useState<number>(0);
  const [copying, setCopying] = useState(false);
  const { isOpen: isCopyOpen, onOpen: onCopyOpen, onClose: onCopyClose } = useDisclosure();

  // Batch copy-to-next-year
  const [selectedRiskIds, setSelectedRiskIds] = useState<Set<string>>(new Set());
  const [selectedMasterCodes, setSelectedMasterCodes] = useState<Set<string>>(new Set());
  const [batchCopying, setBatchCopying] = useState(false);
  const { isOpen: isBatchCopyOpen, onOpen: onBatchCopyOpen, onClose: onBatchCopyClose } = useDisclosure();

  // Report result modal (รายงานผลดำเนินการ)
  const { isOpen: isReportOpen, onOpen: onReportOpen, onClose: onReportClose } = useDisclosure();
  const [reportData, setReportData] = useState({
    kri_id: '',
    kri_name: '',
    target_value: '',
    unit: '',
    actual_result: '',
    measurement_date: '',
    output: '',
    risk_id: '',
  });

  // Risk Master modal (add/edit master risk concept)
  const { isOpen: isMasterModalOpen, onOpen: onMasterModalOpen, onClose: onMasterModalClose } = useDisclosure();
  const [masterModalData, setMasterModalData] = useState({ risk_code: '', risk_name: '', risk_type_id: 0, strategy_id: 0 });
  const [editingMasterCode, setEditingMasterCode] = useState<string | null>(null);

  // Assessment modal (add/edit risk-year-department under a master)
  const { isOpen: isAssessModalOpen, onOpen: onAssessModalOpen, onClose: onAssessModalClose } = useDisclosure();
  const [assessModalData, setAssessModalData] = useState<any>({});
  const [editingAssessRiskId, setEditingAssessRiskId] = useState<string | null>(null);

  const openEditMasterModal = (master: MasterRisk) => {
    setEditingMasterCode(master.risk_code);
    setMasterModalData({ risk_code: master.risk_code, risk_name: master.risk_name, risk_type_id: master.risk_type_id, strategy_id: master.strategy_id });
    onMasterModalOpen();
  };

  const handleSaveMaster = async () => {
    try {
      if (editingMasterCode) {
        await masterRiskApi.update(editingMasterCode, masterModalData);
        toast({ title: t('section.updated'), status: 'success' });
      } else {
        await masterRiskApi.create(masterModalData);
        toast({ title: t('section.added'), status: 'success' });
      }
      onMasterModalClose();
      fetchData();
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const handleDeleteMaster = async (riskCode: string) => {
    if (!window.confirm('ลบ master risk นี้?')) return;
    try {
      await masterRiskApi.delete(riskCode);
      toast({ title: t('section.deleted'), status: 'success' });
      fetchData();
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const openAddAssessModal = (masterCode: string) => {
    setEditingAssessRiskId(null);
    const master = masterRisks.find((m) => m.risk_code === masterCode);
    const nextId = `RM-${masterCode}-${Date.now()}`;
    setAssessModalData({
      risk_id: nextId,
      risk_code: masterCode,
      risk_name: master?.risk_name || '',
      unit_id: isAdmin ? '' : (user?.unit_id || ''),
      fiscal_year_id: fiscalYears.find((fy) => fy.is_active)?.id || 0,
      risk_owner: '',
      risk_type_id: master?.risk_type_id || 0,
      strategy_id: master?.strategy_id || 0,
      risk_management_type_id: 0,
      inherent_likelihood: 1,
      inherent_impact: 1,
      residual_likelihood: null,
      residual_impact: null,
      erm_level: 'department',
      impact_type: '',
      impact_to: '',
      output: '',
      assessment_result: '',
      plan_start_month: null,
      plan_end_month: null,
    });
    onAssessModalOpen();
  };

  const openEditAssessModal = (risk: Risk) => {
    setEditingAssessRiskId(risk.risk_id);
    setAssessModalData({
      risk_id: risk.risk_id,
      risk_code: risk.risk_code,
      risk_name: risk.risk_name || '',
      unit_id: risk.unit_id,
      fiscal_year_id: risk.fiscal_year_id,
      risk_owner: risk.risk_owner || '',
      risk_type_id: risk.risk_type_id,
      strategy_id: risk.strategy_id,
      risk_management_type_id: risk.risk_management_type_id,
      inherent_likelihood: risk.inherent_likelihood,
      inherent_impact: risk.inherent_impact,
      residual_likelihood: risk.residual_likelihood,
      residual_impact: risk.residual_impact,
      erm_level: risk.erm_level,
      impact_type: risk.impact_type || '',
      impact_to: risk.impact_to || '',
      output: risk.output || '',
      assessment_result: risk.assessment_result || '',
      plan_start_month: risk.plan_start_month,
      plan_end_month: risk.plan_end_month,
    });
    onAssessModalOpen();
  };

  const handleSaveAssess = async () => {
    const data = { ...assessModalData };
    data.inherent_risk = data.inherent_likelihood * data.inherent_impact;
    if (data.residual_likelihood && data.residual_impact) {
      data.residual_risk = data.residual_likelihood * data.residual_impact;
    }
    try {
      if (editingAssessRiskId) {
        await riskApi.update(editingAssessRiskId, data);
        toast({ title: t('section.updated'), status: 'success' });
      } else {
        await riskApi.create(data);
        toast({ title: t('section.added'), status: 'success' });
      }
      onAssessModalClose();
      fetchData();
      if (expandedMasterId) {
        const res = await masterRiskApi.getRisks(expandedMasterId);
        setMasterYearRisks(res.data);
      }
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [risksRes, deptsRes, fyRes, ctRes, masterRes, rtRes, stRes, rmtRes] = await Promise.allSettled([
        riskApi.getAll(),
        departmentApi.getAll(),
        lookupApi.getFiscalYears(),
        lookupApi.getControlTypes(),
        masterRiskApi.getAll(),
        lookupApi.getRiskTypes(),
        lookupApi.getStrategies(),
        lookupApi.getRiskManagementTypes(),
      ]);
      if (risksRes.status === 'fulfilled') setRisks(risksRes.value.data);
      if (deptsRes.status === 'fulfilled') setDepartments(deptsRes.value.data);
      if (fyRes.status === 'fulfilled') {
        setFiscalYears(fyRes.value.data);
      }
      if (ctRes.status === 'fulfilled') setControlTypes(ctRes.value.data);
      if (masterRes.status === 'fulfilled') setMasterRisks(masterRes.value.data);
      if (rtRes.status === 'fulfilled') setRiskTypes(rtRes.value.data);
      if (stRes.status === 'fulfilled') setStrategies(stRes.value.data);
      if (rmtRes.status === 'fulfilled') setRiskMgmtTypes(rmtRes.value.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Regular users are locked to their own department; manager sees all
  useEffect(() => {
    if (!isAdmin && !isManager && user?.unit_id) {
      setSelectedUnitId(user.unit_id);
    }
  }, [isAdmin, isManager, user]);

  const visibleDepartments = (isAdmin || isManager)
    ? departments
    : departments.filter((d) => d.unit_id === user?.unit_id);

  const getNextFiscalYearId = (fyId: number | null | undefined): number => {
    const current = fiscalYears.find((fy) => fy.id === fyId);
    if (!current) return 0;
    const next = fiscalYears.find((fy) => fy.year === current.year + 1);
    return next ? next.id : 0;
  };

  const filteredRisks = risks.filter((r) => {
    if (selectedUnitId && r.unit_id !== selectedUnitId) return false;
    if (selectedFiscalYear && r.fiscal_year_id !== Number(selectedFiscalYear)) return false;
    if (selectedErmLevel && r.erm_level !== selectedErmLevel) return false;
    return true;
  });

  // Master view: show master only if it has at least one risk record matching filters
  const filteredMasters = masterRisks.filter((m) => {
    if (!selectedUnitId && !selectedFiscalYear && !selectedErmLevel) return true;
    return risks.some((r) => {
      if (r.risk_code !== m.risk_code) return false;
      if (selectedUnitId && r.unit_id !== selectedUnitId) return false;
      if (selectedFiscalYear && r.fiscal_year_id !== Number(selectedFiscalYear)) return false;
      if (selectedErmLevel && r.erm_level !== selectedErmLevel) return false;
      return true;
    });
  });

  const risksByDept = filteredRisks.reduce<Record<string, Risk[]>>((acc, r) => {
    const dept = departments.find((d) => d.unit_id === r.unit_id);
    const key = dept
      ? `${dept.unit_id} — ${dept.work_unit ? `${dept.department_name} (${dept.work_unit})` : dept.department_name}`
      : r.unit_id;
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  const toggleExpand = async (risk: Risk) => {
    if (expandedRisks[risk.risk_id]) {
      const next = { ...expandedRisks };
      delete next[risk.risk_id];
      setExpandedRisks(next);
    } else {
      setLoadingRisk(risk.risk_id);
      try {
        const res = await riskApi.getByRiskId(risk.risk_id);
        setExpandedRisks((prev) => ({ ...prev, [risk.risk_id]: res.data }));
      } catch (error) {
        toast({ title: t('riskRegister.loadError'), status: 'error' });
      } finally {
        setLoadingRisk(null);
      }
    }
  };

  const refreshRisk = async (riskId: string) => {
    try {
      const res = await riskApi.getByRiskId(riskId);
      setExpandedRisks((prev) => ({ ...prev, [riskId]: res.data }));
    } catch (error) {}
  };

  // Master expanded year risk details (risk_id -> full detail)
  const [masterExpandedDetails, setMasterExpandedDetails] = useState<Record<string, RiskFull>>({});

  const toggleExpandMaster = async (masterCode: string) => {
    if (expandedMasterId === masterCode) {
      setExpandedMasterId(null);
      setMasterYearRisks([]);
      setMasterExpandedDetails({});
    } else {
      setLoadingMaster(true);
      try {
        const res = await masterRiskApi.getRisks(masterCode);
        const risks: Risk[] = res.data;
        setMasterYearRisks(risks);
        setExpandedMasterId(masterCode);
        const details: Record<string, RiskFull> = {};
        await Promise.allSettled(
          risks.map(async (r) => {
            try {
              const detailRes = await riskApi.getByRiskId(r.risk_id);
              details[r.risk_id] = detailRes.data;
            } catch {}
          })
        );
        setMasterExpandedDetails(details);
      } catch (error) {
        toast({ title: t('riskRegister.loadError'), status: 'error' });
      } finally {
        setLoadingMaster(false);
      }
    }
  };

  const toggleMasterYearRisk = async (riskId: string) => {
    if (masterExpandedDetails[riskId]) {
      setMasterExpandedDetails((prev) => { const n = { ...prev }; delete n[riskId]; return n; });
    } else {
      try {
        const res = await riskApi.getByRiskId(riskId);
        setMasterExpandedDetails((prev) => ({ ...prev, [riskId]: res.data }));
      } catch {
        toast({ title: t('riskRegister.loadError'), status: 'error' });
      }
    }
  };

  const refreshMasterDetail = async (riskId: string) => {
    try {
      const res = await riskApi.getByRiskId(riskId);
      setMasterExpandedDetails((prev) => ({ ...prev, [riskId]: res.data }));
    } catch {}
  };

  const openControlModal = async (detail: RiskFull, kind: 'IC' | 'AC' | 'KRI') => {
    setActiveRiskId(detail.risk_id);
    setActiveFiscalYearId(detail.fiscal_year_id);
    const defaultCT = controlTypes.length > 0 ? controlTypes[0].id : 1;
    if (kind === 'IC') {
      const nextNo = (detail.internal_controls?.length || 0) + 1;
      setAddICData({ ic_id: '', ic_no: nextNo, control_description: '', ic_owner: '', control_type_id: defaultCT, evidence_link: '', evidence_name: '' });
      onAddICOpen();
    } else if (kind === 'AC') {
      const nextNo = (detail.additional_controls?.length || 0) + 1;
      setAddACData({ ac_id: '', ac_no: nextNo, control_description: '', ac_owner: '', control_type_id: defaultCT, evidence_link: '', evidence_name: '' });
      onAddACOpen();
    } else onAddKRIOpen();
  };

  const handleDelete = async (riskId: string) => {
    // Admin/manager: direct delete with confirmation
    if (isAdmin || isManager) {
      if (!window.confirm(t('riskRegister.confirmDelete'))) return;
      try {
        await riskApi.delete(riskId);
        setSelectedRiskIds((prev) => {
          const next = new Set(prev);
          next.delete(riskId);
          return next;
        });
        toast({ title: t('riskRegister.deleted'), status: 'success' });
        fetchData();
      } catch (error) {
        toast({ title: t('riskRegister.deleteError'), status: 'error' });
      }
      return;
    }
    // Regular user: request delete (sends to manager for approval)
    try {
      await riskApi.requestDelete(riskId);
      toast({ title: t('riskRegister.requestDeleteSuccess'), status: 'success' });
      fetchData();
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('riskRegister.requestDeleteError'), status: 'error' });
    }
  };

  const handleApproveDelete = async (riskId: string) => {
    try {
      await riskApi.approveDelete(riskId);
      toast({ title: t('riskRegister.approveDeleteSuccess'), status: 'success' });
      fetchData();
      fetchPendingDeletions();
    } catch (error) {
      toast({ title: t('riskRegister.deleteError'), status: 'error' });
    }
  };

  const handleRejectDelete = async (riskId: string) => {
    try {
      await riskApi.rejectDelete(riskId);
      toast({ title: t('riskRegister.rejectDeleteSuccess'), status: 'success' });
      fetchData();
      fetchPendingDeletions();
    } catch (error) {
      toast({ title: t('riskRegister.deleteError'), status: 'error' });
    }
  };

  // Pending deletions for admin/manager
  const [pendingDeletions, setPendingDeletions] = useState<any[]>([]);
  const fetchPendingDeletions = useCallback(async () => {
    if (!isAdmin && !isManager) return;
    try {
      const res = await riskApi.getPendingDeletions();
      setPendingDeletions(res.data);
    } catch (error) {}
  }, [isAdmin, isManager]);

  useEffect(() => { fetchPendingDeletions(); }, [fetchPendingDeletions]);

  const openCopyModal = (risk: Risk) => {
    const nextId = getNextFiscalYearId(risk.fiscal_year_id);
    if (!nextId) {
      const currentYear = fiscalYears.find((fy) => fy.id === risk.fiscal_year_id)?.year;
      toast({ title: t('copy.noNextYear', { year: (currentYear || 0) + 1 }), status: 'warning' });
      return;
    }
    setCopyTarget(risk);
    setCopyYearId(nextId);
    onCopyOpen();
  };

  const handleCopy = async () => {
    if (!copyTarget || !copyYearId) {
      toast({ title: t('copy.selectTargetWarning'), status: 'warning' });
      return;
    }
    setCopying(true);
    try {
      const res = await riskApi.copyRisk({
        source_risk_id: copyTarget.risk_id,
        target_fiscal_year_id: copyYearId,
      });
      toast({ title: t('copy.success', { year: res.data.fiscal_year?.year ?? '-' }), status: 'success' });
      onCopyClose();
      fetchData();
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    } finally {
      setCopying(false);
    }
  };

  const toggleSelect = (riskId: string) => {
    setSelectedRiskIds((prev) => {
      const next = new Set(prev);
      if (next.has(riskId)) next.delete(riskId);
      else next.add(riskId);
      return next;
    });
  };

  const toggleSelectDept = (deptRisks: Risk[]) => {
    setSelectedRiskIds((prev) => {
      const next = new Set(prev);
      const allSelected = deptRisks.length > 0 && deptRisks.every((r) => next.has(r.risk_id));
      deptRisks.forEach((r) => { if (allSelected) next.delete(r.risk_id); else next.add(r.risk_id); });
      return next;
    });
  };

  const toggleSelectMaster = (riskCode: string) => {
    setSelectedMasterCodes((prev) => {
      const next = new Set(prev);
      if (next.has(riskCode)) next.delete(riskCode);
      else next.add(riskCode);
      return next;
    });
  };

  const toggleSelectAllMasters = (masters: MasterRisk[]) => {
    setSelectedMasterCodes((prev) => {
      const next = new Set(prev);
      const allSelected = masters.length > 0 && masters.every((m) => next.has(m.risk_code));
      masters.forEach((m) => { if (allSelected) next.delete(m.risk_code); else next.add(m.risk_code); });
      return next;
    });
  };

  const selectedRisks = risks.filter((r) => selectedRiskIds.has(r.risk_id));

  const handleBatchCopy = async () => {
    setBatchCopying(true);
    let copied = 0;
    let skipped = 0;
    let dropped = 0;
    let noNextYear = 0;
    const failed: string[] = [];
    // Collect risk IDs to copy: from selectedRiskIds (all view) or from master code selections (master view)
    const riskIdsToCopy: string[] = [];
    if (viewMode === 'all') {
      riskIdsToCopy.push(...Array.from(selectedRiskIds));
    } else {
      // Master view: expand selected master codes to all their year-dept risks
      for (const code of Array.from(selectedMasterCodes)) {
        const masterRisks = masterYearRisks.filter((r) => r.risk_code === code && expandedMasterId === code);
        if (masterRisks.length > 0) {
          riskIdsToCopy.push(...masterRisks.map((r) => r.risk_id));
        } else {
          // Master not expanded yet — find from all risks
          const allMasterRisks = risks.filter((r) => r.risk_code === code);
          riskIdsToCopy.push(...allMasterRisks.map((r) => r.risk_id));
        }
      }
    }
    for (const riskId of riskIdsToCopy) {
      const risk = risks.find((r) => r.risk_id === riskId);
      const targetYearId = risk ? getNextFiscalYearId(risk.fiscal_year_id) : 0;
      if (!risk || !targetYearId) {
        if (!risk) dropped++;
        else noNextYear++;
        continue;
      }
      try {
        await riskApi.copyRisk({ source_risk_id: riskId, target_fiscal_year_id: targetYearId });
        copied++;
      } catch (error: any) {
        const detail = error.response?.data?.detail;
        if (typeof detail === 'string' && detail.includes('already carried over')) {
          skipped++;
        } else if (error.response?.status === 404) {
          dropped++;
          setSelectedRiskIds((prev) => { const next = new Set(prev); next.delete(riskId); return next; });
        } else {
          failed.push(risk ? `${risk.risk_code} (${risk.risk_id})` : riskId);
        }
      }
    }
    const parts = [t('copy.copied', { n: copied }), t('copy.alreadyExisted', { n: skipped })];
    if (dropped) parts.push(t('copy.noLongerExists', { n: dropped }));
    if (noNextYear) parts.push(t('copy.noNextYearCount', { n: noNextYear }));
    if (failed.length) parts.push(t('copy.failed', { list: failed.join(', ') }));
    toast({
      title: copied > 0 ? t('copy.complete') : t('copy.nothing'),
      description: parts.join(' | '),
      status: failed.length ? 'warning' : 'success',
      duration: 8000,
      isClosable: true,
    });
    setSelectedRiskIds(new Set());
    setSelectedMasterCodes(new Set());
    setBatchCopying(false);
    onBatchCopyClose();
    fetchData();
  };

  const handleToggleErm = async (risk: Risk) => {
    const isErm = risk.erm_level === 'faculty';
    try {
      if (isErm) {
        await riskApi.unregisterErm(risk.risk_id);
        toast({ title: t('riskRegister.removeErm'), status: 'success' });
      } else {
        await riskApi.registerErm(risk.risk_id);
        toast({ title: t('riskRegister.elevateErm'), status: 'success' });
      }
      fetchData();
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const handleAddCause = async () => {
    try {
      await riskApi.addCause(activeRiskId, addCauseData);
      toast({ title: t('section.added'), status: 'success' });
      onAddCauseClose();
      setAddCauseData({ cause_id: '', cause_number: 1, cause_description: '' });
      refreshRisk(activeRiskId);
      refreshMasterDetail(activeRiskId);
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const handleAddIC = async () => {
    try {
      await riskApi.addIC(activeRiskId, addICData);
      toast({ title: t('section.added'), status: 'success' });
      onAddICClose();
      setAddICData({ ic_id: '', ic_no: 1, control_description: '', ic_owner: '', control_type_id: controlTypes.length > 0 ? controlTypes[0].id : 1, evidence_link: '', evidence_name: '' });
      if (activeRiskId) { refreshRisk(activeRiskId); refreshMasterDetail(activeRiskId); }
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const handleAddAC = async () => {
    try {
      await riskApi.addAC(activeRiskId, addACData);
      toast({ title: t('section.added'), status: 'success' });
      onAddACClose();
      setAddACData({ ac_id: '', ac_no: 1, control_description: '', ac_owner: '', control_type_id: controlTypes.length > 0 ? controlTypes[0].id : 1, evidence_link: '', evidence_name: '' });
      if (activeRiskId) { refreshRisk(activeRiskId); refreshMasterDetail(activeRiskId); }
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const handleAddKRI = async () => {
    try {
      await riskApi.addKRI(activeRiskId, addKRIData);
      toast({ title: t('section.added'), status: 'success' });
      onAddKRIClose();
      setAddKRIData({ kri_id: '', kri_name: '', target_value: '', unit: '', kri_owner: '' });
      if (activeRiskId) { refreshRisk(activeRiskId); refreshMasterDetail(activeRiskId); }
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const findRiskForCause = (causeId: number) => {
    for (const [rid, detail] of Object.entries(expandedRisks)) {
      if (detail.causes?.some((c) => c.id === causeId)) return rid;
    }
    for (const [rid, detail] of Object.entries(masterExpandedDetails)) {
      if (detail.causes?.some((c) => c.id === causeId)) return rid;
    }
    return null;
  };

  const refreshWhereIC = (icId: number) => {
    for (const [rid, detail] of Object.entries(expandedRisks)) {
      if (detail.internal_controls?.some((ic) => ic.id === icId)) { refreshRisk(rid); refreshMasterDetail(rid); return; }
    }
    for (const [rid, detail] of Object.entries(masterExpandedDetails)) {
      if (detail.internal_controls?.some((ic) => ic.id === icId)) { refreshMasterDetail(rid); return; }
    }
  };

  const refreshWhereAC = (acId: number) => {
    for (const [rid, detail] of Object.entries(expandedRisks)) {
      if (detail.additional_controls?.some((ac) => ac.id === acId)) { refreshRisk(rid); refreshMasterDetail(rid); return; }
    }
    for (const [rid, detail] of Object.entries(masterExpandedDetails)) {
      if (detail.additional_controls?.some((ac) => ac.id === acId)) { refreshMasterDetail(rid); return; }
    }
  };

  const refreshWhereKRI = (kriId: number) => {
    for (const [rid, detail] of Object.entries(expandedRisks)) {
      if (detail.kri_records?.some((k) => k.id === kriId)) { refreshRisk(rid); refreshMasterDetail(rid); return; }
    }
    for (const [rid, detail] of Object.entries(masterExpandedDetails)) {
      if (detail.kri_records?.some((k) => k.id === kriId)) { refreshMasterDetail(rid); return; }
    }
  };

  const handleEditCause = async () => {
    try {
      await riskApi.updateCause(editingCauseId, editCauseData);
      toast({ title: t('section.updated'), status: 'success' });
      onEditCauseClose();
      const rid = findRiskForCause(editingCauseId);
      if (rid) { refreshRisk(rid); refreshMasterDetail(rid); }
      for (const [rid, detail] of Object.entries(masterExpandedDetails)) {
        if (detail.causes?.some((c) => c.id === editingCauseId)) { refreshMasterDetail(rid); break; }
      }
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const handleEditIC = async () => {
    try {
      await riskApi.updateIC(editingICId, editICData);
      toast({ title: t('section.updated'), status: 'success' });
      onEditICClose();
      refreshWhereIC(editingICId);
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const handleEditAC = async () => {
    try {
      await riskApi.updateAC(editingACId, editACData);
      toast({ title: t('section.updated'), status: 'success' });
      onEditACClose();
      refreshWhereAC(editingACId);
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const handleEditKRI = async () => {
    try {
      await riskApi.updateKRI(editingKRIId, editKRIData);
      toast({ title: t('section.updated'), status: 'success' });
      onEditKRIClose();
      refreshWhereKRI(editingKRIId);
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const handleReportSave = async () => {
    try {
      // Update KRI actual_result and measurement_date
      await riskApi.updateKRI(Number(reportData.kri_id), {
        actual_result: reportData.actual_result,
        measurement_date: reportData.measurement_date || null,
      });
      // Update risk output
      await riskApi.update(reportData.risk_id, {
        output: reportData.output,
      });
      toast({ title: t('section.updated'), status: 'success' });
      onReportClose();
      refreshRisk(reportData.risk_id);
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const openEditCause = (cause: any) => {
    setEditingCauseId(cause.id);
    setEditCauseData({ cause_id: cause.cause_id, cause_number: cause.cause_number, cause_description: cause.cause_description });
    onEditCauseOpen();
  };

  const openEditIC = (ic: any) => {
    setEditingICId(ic.id);
    setEditICData({ ic_id: ic.ic_id, ic_no: ic.ic_no, control_description: ic.control_description, ic_owner: ic.ic_owner || '', control_type_id: ic.control_type?.id || 1, evidence_link: ic.evidence_link || '', evidence_name: ic.evidence_name || '' });
    for (const [rid, detail] of Object.entries(expandedRisks)) {
      if (detail.internal_controls?.some((i) => i.id === ic.id)) {
        setActiveRiskId(rid);
        setActiveFiscalYearId(detail.fiscal_year_id);
        break;
      }
    }
    onEditICOpen();
  };

  const openEditAC = (ac: any) => {
    setEditingACId(ac.id);
    setEditACData({ ac_id: ac.ac_id, ac_no: ac.ac_no, control_description: ac.control_description, ac_owner: ac.ac_owner || '', control_type_id: ac.control_type?.id || 1, evidence_link: ac.evidence_link || '', evidence_name: ac.evidence_name || '' });
    for (const [rid, detail] of Object.entries(expandedRisks)) {
      if (detail.additional_controls?.some((a) => a.id === ac.id)) {
        setActiveRiskId(rid);
        setActiveFiscalYearId(detail.fiscal_year_id);
        break;
      }
    }
    onEditACOpen();
  };

  const openEditKRI = (kri: any) => {
    setEditingKRIId(kri.id);
    setEditKRIData({ kri_id: kri.kri_id, kri_name: kri.kri_name, target_value: kri.target_value || '', unit: kri.unit || '', kri_owner: kri.kri_owner || '' });
    onEditKRIOpen();
  };

  const openReportModal = (kri: any, riskId: string, currentOutput: string) => {
    setReportData({
      kri_id: kri.id.toString(),
      kri_name: kri.kri_name,
      target_value: kri.target_value || '',
      unit: kri.unit || '',
      actual_result: kri.actual_result || '',
      measurement_date: kri.measurement_date || '',
      output: currentOutput,
      risk_id: riskId,
    });
    onReportOpen();
  };

  const handleDeleteCause = async (causeId: number) => {
    if (!window.confirm(t('section.confirmDeleteCause'))) return;
    try {
      await riskApi.deleteCause(causeId);
      toast({ title: t('section.deleted'), status: 'success' });
      const rid = findRiskForCause(causeId);
      if (rid) { refreshRisk(rid); refreshMasterDetail(rid); }
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const handleDeleteIC = async (icId: number) => {
    if (!window.confirm(t('section.confirmDeleteIc'))) return;
    try {
      await riskApi.deleteIC(icId);
      toast({ title: t('section.deleted'), status: 'success' });
      refreshWhereIC(icId);
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const handleDeleteAC = async (acId: number) => {
    if (!window.confirm(t('section.confirmDeleteAc'))) return;
    try {
      await riskApi.deleteAC(acId);
      toast({ title: t('section.deleted'), status: 'success' });
      refreshWhereAC(acId);
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  const handleDeleteKRI = async (kriId: number) => {
    if (!window.confirm(t('section.confirmDeleteKri'))) return;
    try {
      await riskApi.deleteKRI(kriId);
      toast({ title: t('section.deleted'), status: 'success' });
      refreshWhereKRI(kriId);
    } catch (error: any) {
      toast({ title: error.response?.data?.detail || t('section.error'), status: 'error' });
    }
  };

  if (loading) return <Spinner size="xl" display="block" mx="auto" mt={10} />;

  return (
    <Box>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <HStack justifyContent="space-between" flexWrap="wrap">
          <Heading size="lg" color="blue.600">{t('riskRegister.title')}</Heading>
          {!isGuest && (
            <Button leftIcon={<AddIcon />} colorScheme="blue" onClick={() => navigate('/risk-register/new')}>
              {t('riskRegister.addRisk')}
            </Button>
          )}
        </HStack>

        {/* Filters */}
        <HStack spacing={4} flexWrap="wrap">
          <Select placeholder={t('dashboard.allDepartments')} value={(isAdmin || isManager) ? selectedUnitId : (user?.unit_id || '')} onChange={(e) => { if (isAdmin || isManager) setSelectedUnitId(e.target.value); }} maxW="300px" size="sm" isDisabled={!isAdmin && !isManager}>
            {visibleDepartments.map((d) => (
              <option key={d.unit_id} value={d.unit_id}>
                {d.work_unit ? `${d.department_name} - ${d.work_unit}` : d.department_name}
              </option>
            ))}
          </Select>
          <Select placeholder={t('dashboard.allFiscalYears')} value={selectedFiscalYear} onChange={(e) => setSelectedFiscalYear(e.target.value)} maxW="200px" size="sm">
            {fiscalYears.map((fy) => (<option key={fy.id} value={fy.id}>{fy.year}</option>))}
          </Select>
          <Select placeholder={t('dashboard.allLevels')} value={selectedErmLevel} onChange={(e) => setSelectedErmLevel(e.target.value)} maxW="200px" size="sm">
            <option value="department">{t('dashboard.levelDepartment')}</option>
            <option value="faculty">{t('dashboard.levelFaculty')}</option>
          </Select>
          <ButtonGroup size="sm" isAttached variant="outline">
            <Button onClick={() => setViewMode('master')} variant={viewMode === 'master' ? 'solid' : 'outline'} colorScheme={viewMode === 'master' ? 'blue' : 'gray'}>
              {t('common.masterView')}
            </Button>
            <Button onClick={() => setViewMode('all')} variant={viewMode === 'all' ? 'solid' : 'outline'} colorScheme={viewMode === 'all' ? 'blue' : 'gray'}>
              {t('common.allView')}
            </Button>
          </ButtonGroup>
          {!isGuest && (
            <Button size="sm" colorScheme="teal" variant="outline" leftIcon={<CopyIcon />}
              isDisabled={viewMode === 'master' ? selectedMasterCodes.size === 0 : selectedRiskIds.size === 0}
              onClick={onBatchCopyOpen}>
              {t('riskRegister.copySelected', { n: viewMode === 'master' ? selectedMasterCodes.size : selectedRiskIds.size })}
            </Button>
          )}
          <Text color="gray.500" fontSize="sm">{viewMode === 'master' ? t('riskRegister.totalMasters', { n: filteredMasters.length }) : t('riskRegister.totalRisks', { n: filteredRisks.length })}</Text>
        </HStack>

        {/* Pending Deletions (admin/manager only) */}
        {(isAdmin || isManager) && pendingDeletions.length > 0 && (
          <Box bg="red.50" borderRadius="lg" p={4} border="1px solid" borderColor="red.200">
            <Text fontWeight="bold" color="red.600" mb={3}>{t('riskRegister.pendingDeletions', { n: pendingDeletions.length })}</Text>
            <VStack spacing={2} align="stretch">
              {pendingDeletions.map((r: any) => (
                <HStack key={r.risk_id} justifyContent="space-between" bg="white" p={2} borderRadius="md">
                  <VStack align="start" spacing={0}>
                    <Text fontSize="sm" fontWeight="bold">{r.risk_code} — {r.risk_name}</Text>
                    <Text fontSize="xs" color="gray.500">ส่งคำขอโดย: {r.pending_deleter_name || '-'} | {r.department ? (r.department.work_unit ? `${r.department.department_name} - ${r.department.work_unit}` : r.department.department_name) : r.unit_id}</Text>
                  </VStack>
                  <HStack spacing={2}>
                    <Button size="xs" colorScheme="red" onClick={() => handleApproveDelete(r.risk_id)}>{t('riskRegister.approveDelete')}</Button>
                    <Button size="xs" colorScheme="gray" variant="outline" onClick={() => handleRejectDelete(r.risk_id)}>{t('riskRegister.rejectDelete')}</Button>
                  </HStack>
                </HStack>
              ))}
            </VStack>
          </Box>
        )}

        {/* Department Groups - All Records View */}
        {viewMode === 'all' && Object.entries(risksByDept).map(([deptKey, deptRisks]) => {
          return (
            <Box key={deptKey} bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
              {/* Department Header */}
              <Box px={4} py={3} bg="blue.600" color="white">
                <HStack justifyContent="space-between">
                  <Text fontWeight="bold">{deptKey}</Text>
                  <Badge colorScheme="whiteAlpha" borderRadius="full">{t('riskRegister.risksCount', { n: deptRisks.length })}</Badge>
                </HStack>
              </Box>

              {/* Risk Table */}
              <Box overflowX="auto">
              <Table variant="simple" size="sm" style={{ tableLayout: 'fixed', width: '100%' }}>
                <Thead bg="gray.100">
                  <Tr>
                    <Th width="40px" px={2}>
                      <Checkbox
                        isChecked={deptRisks.length > 0 && deptRisks.every((r) => selectedRiskIds.has(r.risk_id))}
                        isIndeterminate={deptRisks.some((r) => selectedRiskIds.has(r.risk_id)) && !deptRisks.every((r) => selectedRiskIds.has(r.risk_id))}
                        onChange={() => toggleSelectDept(deptRisks)}
                        aria-label={t('riskRegister.selectAllDept')}
                      />
                    </Th>
                    <Th width="120px" px={2}>{t('common.riskCode')}</Th>
                    <Th px={2}>{t('common.riskName')}</Th>
                    <Th width="100px" px={2}>{t('common.ermLevel')}</Th>
                    <Th width="130px" px={2}>{t('common.type')}</Th>
                    <Th width="130px" px={2}>{t('common.strategy')}</Th>
                    <Th width="120px" px={2}>{t('common.owner')}</Th>
                    <Th width="85px" isNumeric px={2}>{t('riskRegister.inherent')}</Th>
                    <Th width="85px" isNumeric px={2}>{t('riskRegister.residual')}</Th>
                    <Th width="190px" textAlign="center" px={2}>{t('common.actions')}</Th>                  </Tr>
                </Thead>
                <Tbody>
                  {deptRisks.map((risk) => {
                    const level = getRiskLevelStyle(risk.inherent_likelihood, risk.inherent_impact);
                    const isExpanded = !!expandedRisks[risk.risk_id];
                    const detail = expandedRisks[risk.risk_id];
                    const isLoading = loadingRisk === risk.risk_id;

                    return (
                      <React.Fragment key={risk.risk_id}>
                        <Tr _hover={{ bg: 'gray.50' }} bg={isExpanded ? 'blue.50' : 'white'}>
                          <Td px={2}>
                            <HStack spacing={1}>
                              <Checkbox
                                isChecked={selectedRiskIds.has(risk.risk_id)}
                                onChange={() => toggleSelect(risk.risk_id)}
                                aria-label={t('riskRegister.selectRisk', { code: risk.risk_code })}
                              />
                              <IconButton
                                aria-label="Expand"
                                icon={isLoading ? <Spinner size="xs" /> : isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                size="xs"
                                variant="ghost"
                                onClick={() => toggleExpand(risk)}
                              />
                            </HStack>
                          </Td>
                          <Td fontWeight="bold" px={2}>{risk.risk_code}</Td>
                          <Td whiteSpace="normal" px={2}>{risk.risk_name}</Td>
                          <Td px={2}>
                            <Badge colorScheme={risk.erm_level === 'faculty' ? 'purple' : 'blue'} fontSize="xs">
                              {risk.erm_level === 'faculty' ? t('erm.erm') : t('erm.dept')}
                            </Badge>
                          </Td>
                          <Td fontSize="xs" px={2}>{risk.risk_type?.name_th || '-'}</Td>
                          <Td fontSize="xs" px={2}>{risk.strategy?.name || '-'}</Td>
                          <Td fontSize="xs" px={2}>{risk.risk_owner || '-'}</Td>
                          <Td fontWeight="bold" bg={level.bg} color={level.text} px={2} textAlign="center">{getRiskLevelLabel(risk.inherent_likelihood, risk.inherent_impact)}</Td>
                          {risk.residual_risk ? (
                            <Td fontWeight="bold" bg={getRiskLevelStyle(risk.residual_likelihood ?? 0, risk.residual_impact ?? 0).bg} color={getRiskLevelStyle(risk.residual_likelihood ?? 0, risk.residual_impact ?? 0).text} px={2} textAlign="center">{getRiskLevelLabel(risk.residual_likelihood ?? 0, risk.residual_impact ?? 0)}</Td>
                          ) : (
                            <Td isNumeric fontWeight="bold" px={2}>-</Td>
                          )}
                          <Td px={2}>
                            <HStack spacing={1} justifyContent="center">
                              <Tooltip label={t('riskRegister.viewExpand')}><IconButton aria-label="View" icon={<ViewIcon />} size="xs" colorScheme="blue" variant="ghost" onClick={() => toggleExpand(risk)} /></Tooltip>
                              <Tooltip label={t('riskRegister.copyNextYear')}><IconButton aria-label="Copy to Next Year" icon={<CopyIcon />} size="xs" colorScheme="teal" variant="ghost" onClick={() => openCopyModal(risk)} /></Tooltip>
                              {(isAdmin || isManager) && (
                                <Tooltip label={risk.erm_level === 'faculty' ? t('riskRegister.removeErm') : t('riskRegister.elevateErm')}>
                                  <IconButton aria-label="Toggle ERM" icon={<StarIcon />} size="xs" colorScheme={risk.erm_level === 'faculty' ? 'purple' : 'gray'} variant={risk.erm_level === 'faculty' ? 'solid' : 'ghost'} onClick={() => handleToggleErm(risk)} />
                                </Tooltip>
                              )}
                              {!isGuest && (
                                <Tooltip label={t('riskRegister.editAssessment')}><IconButton aria-label="Edit" icon={<EditIcon />} size="xs" colorScheme="blue" variant="ghost" onClick={() => openEditAssessModal(risk)} /></Tooltip>
                              )}
                              {!isGuest && (
                                <Tooltip label={t('riskRegister.delete')}><IconButton aria-label="Delete" icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={() => handleDelete(risk.risk_id)} /></Tooltip>
                              )}
                            </HStack>
                          </Td>
                        </Tr>

                        {/* Expanded Detail Row */}
                        {isExpanded && detail && (
                          <Tr>
                            <Td colSpan={10} p={0}>
                              <Box px={6} py={4} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
                                {/* Risk Info Grid */}
                                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
                                  <Box><Text fontSize="xs" color="gray.500">{t('common.department')}</Text><Text fontWeight="bold" fontSize="sm">{detail.department ? (detail.department.work_unit ? `${detail.department.department_name} - ${detail.department.work_unit}` : detail.department.department_name) : detail.unit_id}</Text></Box>
                                  <Box><Text fontSize="xs" color="gray.500">{t('common.fiscalYear')}</Text><Text fontWeight="bold" fontSize="sm">{detail.fiscal_year?.year || '-'}</Text></Box>
                                  <Box><Text fontSize="xs" color="gray.500">{t('common.ermLevel')}</Text>
                                    <Badge colorScheme={detail.erm_level === 'faculty' ? 'purple' : 'blue'}>{detail.erm_level === 'faculty' ? t('erm.faculty') : t('erm.department')}</Badge>
                                  </Box>
                                  <Box><Text fontSize="xs" color="gray.500">{t('common.riskType')}</Text><Text fontWeight="bold" fontSize="sm">{detail.risk_type?.name_th || '-'}</Text></Box>
                                  <Box><Text fontSize="xs" color="gray.500">{t('common.strategy')}</Text><Text fontWeight="bold" fontSize="sm">{detail.strategy?.name || '-'}</Text></Box>
                                  <Box><Text fontSize="xs" color="gray.500">{t('common.riskCode')}</Text><Text fontWeight="bold" fontSize="sm">{detail.risk_code}</Text></Box>
                                  <Box><Text fontSize="xs" color="gray.500">{t('riskRegister.riskManagementType')}</Text><Text fontWeight="bold" fontSize="sm">{detail.risk_management_type?.name || '-'}</Text></Box>
                                  <Box><Text fontSize="xs" color="gray.500">{t('riskRegister.inherentLikelihood')}</Text><Text fontWeight="bold" fontSize="sm">{detail.inherent_likelihood}</Text></Box>
                                  <Box><Text fontSize="xs" color="gray.500">{t('riskRegister.inherentImpact')}</Text><Text fontWeight="bold" fontSize="sm">{detail.inherent_impact}</Text></Box>
                                  {detail.impact_type && <Box><Text fontSize="xs" color="gray.500">{t('common.impactType')}</Text><Text fontWeight="bold" fontSize="sm">{detail.impact_type}</Text></Box>}
                                  {detail.impact_to && <Box><Text fontSize="xs" color="gray.500">{t('common.impactTo')}</Text><Text fontWeight="bold" fontSize="sm">{detail.impact_to}</Text></Box>}
                                  {detail.assessment_result && <Box><Text fontSize="xs" color="gray.500">{t('common.assessmentResult')}</Text><Text fontWeight="bold" fontSize="sm">{detail.assessment_result}</Text></Box>}
                                  {detail.plan_start_month && detail.plan_end_month && detail.fiscal_year && (() => {
                                    const THAI_MONTHS = ['', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
                                    const startYear = detail.plan_start_month >= 10 ? detail.fiscal_year.year - 1 : detail.fiscal_year.year;
                                    const endYear = detail.plan_end_month <= 9 ? detail.fiscal_year.year : detail.fiscal_year.year + 1;
                                    return (
                                      <Box><Text fontSize="xs" color="gray.500">{t('common.planPeriod')}</Text><Text fontWeight="bold" fontSize="sm">{THAI_MONTHS[detail.plan_start_month]} {startYear} ถึง {THAI_MONTHS[detail.plan_end_month]} {endYear}</Text></Box>
                                    );
                                  })()}
                                  {detail.output && <Box gridColumn={{ md: 'span 2' }}><Text fontSize="xs" color="gray.500">{t('common.output')}</Text><Text fontWeight="bold" fontSize="sm">{detail.output}</Text></Box>}
                                </SimpleGrid>

                                {/* 1. Causes */}
                                <Box mb={4}>
                                  <HStack justifyContent="space-between" mb={2}>
                                    <Text fontWeight="bold" fontSize="sm" color="orange.600">
                                      {t('section.causes', { n: detail.causes?.length || 0 })}
                                    </Text>
                                    {!isGuest && (
                                      <Button size="xs" leftIcon={<AddIcon />} colorScheme="orange" variant="outline"
                                        onClick={() => {
                                          setActiveRiskId(detail.risk_id);
                                          const maxNo = (detail.causes || []).reduce((max, c) => Math.max(max, c.cause_number), 0);
                                          setAddCauseData({ cause_id: '', cause_number: maxNo + 1, cause_description: '' });
                                          onAddCauseOpen();
                                        }}>
                                        {t('section.addCause')}
                                      </Button>
                                    )}
                                  </HStack>
                                  {detail.causes && detail.causes.length > 0 ? (
                                    <Accordion allowMultiple size="sm">
                                      {detail.causes.map((cause) => (
                                        <AccordionItem key={cause.id} bg="white" borderRadius="md" mb={1} border="1px" borderColor="gray.200">
                                          <AccordionButton px={3} py={2}>
                                            <Box flex="1" textAlign="left">
                                              <HStack spacing={3}>
                                                <Badge colorScheme="orange">{cause.cause_id}</Badge>
                                                <Text fontSize="sm" fontWeight="bold">{t('section.cause')} {cause.cause_number}</Text>
                                                <Text fontSize="xs" color="gray.500">RC: {cause.risk_id}</Text>
                                              </HStack>
                                              <Text fontSize="xs" color="gray.600" mt={1}>{cause.cause_description}</Text>
                                            </Box>
                                             <HStack spacing={1} mr={2}>
                                                {!isGuest && (
                                                  <Tooltip label={t('riskRegister.edit')}><IconButton aria-label="Edit" icon={<EditIcon />} size="xs" colorScheme="yellow" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditCause(cause); }} /></Tooltip>
                                                )}
                                                {!isGuest && (
                                                  <Tooltip label={t('riskRegister.delete')}><IconButton aria-label="Delete" icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteCause(cause.id); }} /></Tooltip>
                                                )}
                                              </HStack>
                                            <AccordionIcon />
                                          </AccordionButton>
                                          <AccordionPanel px={3} pb={3}>
                                            <Text fontSize="xs" color="gray.500">Cause ID: {cause.cause_id} | RC: {cause.risk_id}</Text>
                                            <Text fontSize="xs" mt={1}>{cause.cause_description}</Text>
                                          </AccordionPanel>
                                        </AccordionItem>
                                      ))}
                                    </Accordion>
                                  ) : (
                                    <Text fontSize="xs" color="gray.400" fontStyle="italic">{t('section.noCauses')}</Text>
                                  )}
                                </Box>

                                {/* 2. Internal Controls — flat accordion like Causes */}
                                <Box mb={4}>
                                  <HStack justifyContent="space-between" mb={2}>
                                    <Text fontWeight="bold" fontSize="sm" color="green.600">
                                      {t('section.internalControls', { n: detail.internal_controls?.length || 0 })}
                                    </Text>
                                    {!isGuest && (
                                      <Button size="xs" leftIcon={<AddIcon />} colorScheme="green" variant="outline"
                                        onClick={() => openControlModal(detail, 'IC')}>
                                        {t('section.addIc')}
                                      </Button>
                                    )}
                                  </HStack>
                                  {detail.internal_controls && detail.internal_controls.length > 0 ? (
                                    <Accordion allowMultiple size="sm">
                                      {detail.internal_controls.map((ic) => (
                                          <AccordionItem key={ic.id} bg="white" borderRadius="md" mb={1} border="1px" borderColor="green.200">
                                            <AccordionButton px={3} py={2}>
                                              <Box flex="1" textAlign="left">
                                                <HStack spacing={2}>
                                                  <Badge colorScheme="green">{ic.ic_id}</Badge>
                                                  <Text fontSize="sm" fontWeight="bold">IC{ic.ic_no}</Text>
                                                  <Badge colorScheme="teal" fontSize="xs">{ic.control_type?.name || '-'}</Badge>
                                                </HStack>
                                                <Text fontSize="xs" color="gray.600" mt={1}>{ic.control_description}</Text>
                                              </Box>
                                              <HStack spacing={1} mr={2}>
                                                {!isGuest && (
                                                  <Tooltip label={t('riskRegister.edit')}><IconButton aria-label="Edit" icon={<EditIcon />} size="xs" colorScheme="yellow" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditIC(ic); }} /></Tooltip>
                                                )}
                                                {!isGuest && (
                                                  <Tooltip label={t('riskRegister.delete')}><IconButton aria-label="Delete" icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteIC(ic.id); }} /></Tooltip>
                                                )}
                                              </HStack>
                                              <AccordionIcon />
                                            </AccordionButton>
                                            <AccordionPanel px={3} pb={3}>
                                              <Text fontSize="xs">{ic.control_description}</Text>
                                              <Text fontSize="xs" color="gray.500" mt={1}>{t('common.owner')}: {ic.ic_owner || '-'}</Text>
                                              {ic.evidence_link && (
                                                <Box mt={2} p={2} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                                                  <Text fontSize="xs" fontWeight="bold" color="green.700">{t('common.evidence')}:</Text>
                                                  <a href={ic.evidence_link.startsWith('http') ? ic.evidence_link : `${API_BASE_URL}${ic.evidence_link}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#2B6CB0', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <ViewIcon boxSize={3} /> {ic.evidence_name || ic.evidence_link}
                                                  </a>
                                                </Box>
                                              )}
                                            </AccordionPanel>
                                          </AccordionItem>
                                        ))}
                                    </Accordion>
                                  ) : (
                                    <Text fontSize="xs" color="gray.400" fontStyle="italic">{t('section.noIcs')}</Text>
                                  )}
                                </Box>

                                {/* 3. Additional Controls — flat accordion like Causes */}
                                <Box mb={4}>
                                  <HStack justifyContent="space-between" mb={2}>
                                    <Text fontWeight="bold" fontSize="sm" color="yellow.600">
                                      {t('section.additionalControls', { n: detail.additional_controls?.length || 0 })}
                                    </Text>
                                    {!isGuest && (
                                      <Button size="xs" leftIcon={<AddIcon />} colorScheme="yellow" variant="outline"
                                        onClick={() => openControlModal(detail, 'AC')}>
                                        {t('section.addAc')}
                                      </Button>
                                    )}
                                  </HStack>
                                  {detail.additional_controls && detail.additional_controls.length > 0 ? (
                                    <Accordion allowMultiple size="sm">
                                      {detail.additional_controls.map((ac) => (
                                          <AccordionItem key={ac.id} bg="white" borderRadius="md" mb={1} border="1px" borderColor="yellow.200">
                                            <AccordionButton px={3} py={2}>
                                              <Box flex="1" textAlign="left">
                                                <HStack spacing={2}>
                                                  <Badge colorScheme="yellow">{ac.ac_id}</Badge>
                                                  <Text fontSize="sm" fontWeight="bold">AC{ac.ac_no}</Text>
                                                  <Badge colorScheme="orange" fontSize="xs">{ac.control_type?.name || '-'}</Badge>
                                                </HStack>
                                                <Text fontSize="xs" color="gray.600" mt={1}>{ac.control_description}</Text>
                                              </Box>
                                              <HStack spacing={1} mr={2}>
                                                {!isGuest && (
                                                  <Tooltip label={t('riskRegister.edit')}><IconButton aria-label="Edit" icon={<EditIcon />} size="xs" colorScheme="yellow" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditAC(ac); }} /></Tooltip>
                                                )}
                                                {!isGuest && (
                                                  <Tooltip label={t('riskRegister.delete')}><IconButton aria-label="Delete" icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteAC(ac.id); }} /></Tooltip>
                                                )}
                                              </HStack>
                                              <AccordionIcon />
                                            </AccordionButton>
                                            <AccordionPanel px={3} pb={3}>
                                              <Text fontSize="xs">{ac.control_description}</Text>
                                              <Text fontSize="xs" color="gray.500" mt={1}>{t('common.owner')}: {ac.ac_owner || '-'}</Text>
                                              {ac.evidence_link && (
                                                <Box mt={2} p={2} bg="yellow.50" borderRadius="md" border="1px solid" borderColor="yellow.200">
                                                  <Text fontSize="xs" fontWeight="bold" color="yellow.700">{t('common.evidence')}:</Text>
                                                  <a href={ac.evidence_link.startsWith('http') ? ac.evidence_link : `${API_BASE_URL}${ac.evidence_link}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#2B6CB0', textDecoration: 'underline', display: 'flex', alignItems: 'center', gap: '4px' }}>
                                                    <ViewIcon boxSize={3} /> {ac.evidence_name || ac.evidence_link}
                                                  </a>
                                                </Box>
                                              )}
                                            </AccordionPanel>
                                          </AccordionItem>
                                        ))}
                                    </Accordion>
                                  ) : (
                                    <Text fontSize="xs" color="gray.400" fontStyle="italic">{t('section.noAcs')}</Text>
                                  )}
                                </Box>

                                {/* 4. KRI — flat accordion like Causes */}
                                <Box mb={2}>
                                  <HStack justifyContent="space-between" mb={2}>
                                    <Text fontWeight="bold" fontSize="sm" color="purple.600">
                                      {t('section.kri', { n: detail.kri_records?.length || 0 })}
                                    </Text>
                                    {!isGuest && (
                                      <Button size="xs" leftIcon={<AddIcon />} colorScheme="purple" variant="outline"
                                        onClick={() => openControlModal(detail, 'KRI')}>
                                        {t('section.addKri')}
                                      </Button>
                                    )}
                                  </HStack>
                                  {detail.kri_records && detail.kri_records.length > 0 ? (
                                    <Accordion allowMultiple size="sm">
                                      {detail.kri_records.map((kri) => (
                                          <AccordionItem key={kri.id} bg="white" borderRadius="md" mb={1} border="1px" borderColor="purple.200">
                                            <AccordionButton px={3} py={2}>
                                              <Box flex="1" textAlign="left">
                                                <HStack spacing={2}>
                                                  <Badge colorScheme="purple">{kri.kri_id}</Badge>
                                                  <Text fontSize="sm" fontWeight="bold">{kri.kri_name}</Text>
                                                </HStack>
                                                <Text fontSize="xs" color="gray.600" mt={1}>
                                                  {t('section.target')}: {kri.target_value || '-'} {kri.unit || ''} | {t('common.owner')}: {kri.kri_owner || '-'} | {t('section.result')}: {kri.actual_result || '-'} | {t('common.measurementDate')}: {kri.measurement_date || '-'}
                                                </Text>
                                              </Box>
                                              <HStack spacing={1} mr={2}>
                                                {!isGuest && (
                                                  <Tooltip label={t('riskRegister.report')}><IconButton aria-label="Report" icon={<ViewIcon />} size="xs" colorScheme="teal" variant="ghost" onClick={(e) => { e.stopPropagation(); openReportModal(kri, detail.risk_id, detail.output || ''); }} /></Tooltip>
                                                )}
                                                {!isGuest && (
                                                  <Tooltip label={t('riskRegister.edit')}><IconButton aria-label="Edit" icon={<EditIcon />} size="xs" colorScheme="yellow" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditKRI(kri); }} /></Tooltip>
                                                )}
                                                {!isGuest && (
                                                  <Tooltip label={t('riskRegister.delete')}><IconButton aria-label="Delete" icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteKRI(kri.id); }} /></Tooltip>
                                                )}
                                              </HStack>
                                              <AccordionIcon />
                                            </AccordionButton>
                                            <AccordionPanel px={3} pb={3}>
                                              <Text fontSize="xs" color="gray.600">
                                                {t('section.target')}: {kri.target_value || '-'} {kri.unit || ''} | {t('common.owner')}: {kri.kri_owner || '-'} | {t('section.result')}: {kri.actual_result || '-'}
                                              </Text>
                                            </AccordionPanel>
                                          </AccordionItem>
                                        ))}
                                    </Accordion>
                                  ) : (
                                    <Text fontSize="xs" color="gray.400" fontStyle="italic">{t('section.noKris')}</Text>
                                  )}
                                </Box>
                              </Box>
                            </Td>
                          </Tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Tbody>
              </Table>
              </Box>
            </Box>
          );
        })}

        {/* Master Risk View - grouped by strategy, ordered R T H A G */}
        {viewMode === 'master' && (() => {
          const STRAT_ORDER: Record<string, number> = { R: 0, T: 1, H: 2, A: 3, G: 4 };

          const masterByStrategy: Record<string, MasterRisk[]> = {};
          filteredMasters.forEach((m) => {
            const key = m.strategy?.name || `Strategy ${m.strategy_id}`;
            if (!masterByStrategy[key]) masterByStrategy[key] = [];
            masterByStrategy[key].push(m);
          });
          const sortedStrategies = Object.entries(masterByStrategy).sort(([a], [b]) => {
            const codeA = masterByStrategy[a][0]?.strategy?.code || '';
            const codeB = masterByStrategy[b][0]?.strategy?.code || '';
            return (STRAT_ORDER[codeA] ?? 99) - (STRAT_ORDER[codeB] ?? 99);
          });
          return sortedStrategies.map(([strategyKey, strategyMasters]) => (
            <Box key={strategyKey} bg="white" borderRadius="lg" boxShadow="sm" overflow="hidden">
              <Box px={4} py={3} bg="blue.600" color="white">
                <HStack justifyContent="space-between">
                  <Text fontWeight="bold">{strategyKey}</Text>
                  <Badge colorScheme="whiteAlpha" borderRadius="full">{t('riskRegister.risksCount', { n: strategyMasters.length })}</Badge>
                </HStack>
              </Box>
              <Box overflowX="auto">
              <Table variant="simple" size="sm" style={{ tableLayout: 'fixed', width: '100%' }}>
                <Thead bg="gray.100">
                  <Tr>
                    <Th width="40px" px={2}>
                      {!isGuest && <Checkbox isChecked={strategyMasters.length > 0 && strategyMasters.every((m) => selectedMasterCodes.has(m.risk_code))} isIndeterminate={strategyMasters.some((m) => selectedMasterCodes.has(m.risk_code)) && !strategyMasters.every((m) => selectedMasterCodes.has(m.risk_code))} onChange={() => toggleSelectAllMasters(strategyMasters)} />}
                    </Th>
                    <Th width="120px" px={2}>{t('common.riskCode')}</Th>
                    <Th px={2}>{t('common.riskName')}</Th>
                    <Th width="130px" px={2}>{t('common.type')}</Th>
                    <Th width="80px" textAlign="center" px={2}>{t('common.years')}</Th>
                    <Th width="100px" textAlign="center" px={2}>{t('common.actions')}</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {strategyMasters.map((master) => {
                    const isExpanded = expandedMasterId === master.risk_code;
                    return (
                      <React.Fragment key={master.risk_code}>
                        <Tr _hover={{ bg: 'gray.50' }} bg={isExpanded ? 'blue.50' : 'white'}>
                          <Td px={2}>
                            <HStack spacing={1}>
                              {!isGuest && <Checkbox isChecked={selectedMasterCodes.has(master.risk_code)} onChange={() => toggleSelectMaster(master.risk_code)} />}
                              <IconButton
                                aria-label="Expand"
                                icon={loadingMaster && expandedMasterId === master.risk_code ? <Spinner size="xs" /> : isExpanded ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                size="xs"
                                variant="ghost"
                                onClick={() => toggleExpandMaster(master.risk_code)}
                              />
                            </HStack>
                          </Td>
                          <Td fontWeight="bold" px={2}>{master.risk_code}</Td>
                          <Td whiteSpace="normal" px={2}>{master.risk_name}</Td>
                          <Td fontSize="xs" px={2}>{master.risk_type?.name_th || '-'}</Td>
                          <Td textAlign="center" px={2}>
                            <Text fontSize="xs" fontWeight="bold">
                              {risks.filter((r) => {
                                if (r.risk_code !== master.risk_code) return false;
                                if (selectedUnitId && r.unit_id !== selectedUnitId) return false;
                                if (selectedFiscalYear && r.fiscal_year_id !== Number(selectedFiscalYear)) return false;
                                if (selectedErmLevel && r.erm_level !== selectedErmLevel) return false;
                                return true;
                              }).length || '-'}
                            </Text>
                          </Td>
                          <Td px={2}>
                            <HStack spacing={1} justifyContent="center">
                              <Tooltip label={t('riskRegister.viewExpand')}>
                                <IconButton aria-label="View" icon={<ViewIcon />} size="xs" colorScheme="blue" variant="ghost" onClick={() => toggleExpandMaster(master.risk_code)} />
                              </Tooltip>
                              {!isGuest && (
                                <Tooltip label={t('riskRegister.editMasterRisk')}>
                                  <IconButton aria-label="Edit Master" icon={<EditIcon />} size="xs" colorScheme="yellow" variant="ghost" onClick={() => openEditMasterModal(master)} />
                                </Tooltip>
                              )}
                            </HStack>
                          </Td>
                        </Tr>

                        {/* ─── Expanded: FY → Department → Full Detail ─── */}
                        {isExpanded && (
                          <Tr>
                            <Td colSpan={6} p={0}>
                              <Box px={4} py={3} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
                                {loadingMaster ? (
                                  <Spinner size="sm" />
                                ) : masterYearRisks.length === 0 ? (
                                  <VStack spacing={3} py={4}>
                                    <Text color="gray.500" fontSize="sm">No assessments found</Text>
                                    {!isGuest && (
                                      <Button size="sm" leftIcon={<AddIcon />} colorScheme="green" variant="outline" onClick={() => openAddAssessModal(expandedMasterId!)}>
                                        {t('riskRegister.addAssessment')}
                                      </Button>
                                    )}
                                  </VStack>
                                ) : (() => {
                                  // Group by fiscal year
                                  const byFY: Record<number, Risk[]> = {};
                                  masterYearRisks.forEach((yr) => {
                                    const fy = yr.fiscal_year_id;
                                    if (!byFY[fy]) byFY[fy] = [];
                                    byFY[fy].push(yr);
                                  });
                                  const sortedFYs = Object.entries(byFY).sort(([a], [b]) => Number(b) - Number(a));
                                  return sortedFYs.map(([fyId, fyRisks]) => {
                                    const fyYear = fyRisks[0]?.fiscal_year?.year || fyId;
                                    // Group by department within FY
                                    const byDept: Record<string, Risk[]> = {};
                                    fyRisks.forEach((yr) => {
                                      const deptKey = yr.unit_id;
                                      if (!byDept[deptKey]) byDept[deptKey] = [];
                                      byDept[deptKey].push(yr);
                                    });
                                    const sortedDepts = Object.entries(byDept).sort(([a], [b]) => a.localeCompare(b));
                                    return (
                                      <Box key={`${master.risk_code}-${fyId}`} mb={4}>
                                        <HStack mb={2} px={2}>
                                          <Badge colorScheme="blue" fontSize="sm" px={3} py={1}>{t('common.fiscalYear')} {fyYear}</Badge>
                                          <Badge colorScheme="gray" fontSize="xs">{fyRisks.length} {t('riskRegister.risksCount', { n: fyRisks.length }).split(' ').pop()}</Badge>
                                          {!isGuest && (
                                            <Button size="xs" leftIcon={<AddIcon />} colorScheme="green" variant="ghost" onClick={() => openAddAssessModal(master.risk_code)}>
                                              {t('riskRegister.addAssessment')}
                                            </Button>
                                          )}
                                        </HStack>
                                        {sortedDepts.map(([deptKey, deptRisks]) => {
                                          const dept = departments.find((d) => d.unit_id === deptKey);
                                          const deptLabel = dept
                                            ? (dept.work_unit ? `${dept.department_name} (${dept.work_unit})` : dept.department_name)
                                            : deptKey;
                                          return (
                                            <Box key={`${fyId}-${deptKey}`} ml={4} mb={3} bg="white" borderRadius="md" border="1px" borderColor="gray.200" overflow="hidden">
                                              <Box px={3} py={2} bg="gray.100" borderBottom="1px solid" borderColor="gray.200">
                                                <HStack justifyContent="space-between">
                                                  <Text fontWeight="bold" fontSize="sm" color="blue.700">{deptLabel}</Text>
                                                  <Badge colorScheme="blue" fontSize="xs">{deptKey}</Badge>
                                                </HStack>
                                              </Box>
                                              <Box px={3} py={2}>
                                                {deptRisks.map((yr) => {
                                                  const detail = masterExpandedDetails[yr.risk_id];
                                                  const isDetailOpen = !!detail;
                                                  return (
                                                    <Box key={yr.risk_id} mb={2} border="1px" borderColor="gray.100" borderRadius="md" overflow="hidden">
                                                      {/* Year Risk Row */}
                                                      <Box px={3} py={2} _hover={{ bg: 'gray.50' }} cursor="pointer" onClick={() => toggleMasterYearRisk(yr.risk_id)}>
                                                        <HStack justifyContent="space-between">
                                                          <HStack spacing={3}>
                                                            <IconButton
                                                              aria-label="Expand"
                                                              icon={isDetailOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                                              size="xs" variant="ghost"
                                                              onClick={(e) => { e.stopPropagation(); toggleMasterYearRisk(yr.risk_id); }}
                                                            />
                                                            <Badge bg={getRiskLevelStyle(yr.inherent_likelihood, yr.inherent_impact).bg} color={getRiskLevelStyle(yr.inherent_likelihood, yr.inherent_impact).text}>
                                                              {getRiskLevelLabel(yr.inherent_likelihood, yr.inherent_impact)}
                                                            </Badge>
                                                            {yr.residual_risk ? (
                                                              <Badge bg={getRiskLevelStyle(yr.residual_likelihood ?? 0, yr.residual_impact ?? 0).bg} color={getRiskLevelStyle(yr.residual_likelihood ?? 0, yr.residual_impact ?? 0).text}>
                                                                {getRiskLevelLabel(yr.residual_likelihood ?? 0, yr.residual_impact ?? 0)}
                                                              </Badge>
                                                            ) : null}
                                                            <Badge colorScheme={yr.erm_level === 'faculty' ? 'purple' : 'blue'} fontSize="xs">
                                                              {yr.erm_level === 'faculty' ? t('erm.erm') : t('erm.dept')}
                                                            </Badge>
                                                            <Text fontSize="xs" color="gray.600">{yr.risk_owner || '-'}</Text>
                                                          </HStack>
                                                          <HStack spacing={1} onClick={(e) => e.stopPropagation()}>
                                                            {!isGuest && (
                                                              <Tooltip label={t('riskRegister.copyNextYear')}>
                                                                <IconButton aria-label="Copy" icon={<CopyIcon />} size="xs" colorScheme="teal" variant="ghost" onClick={() => openCopyModal(yr)} />
                                                              </Tooltip>
                                                            )}
                                                            {!isGuest && (
                                                              <Tooltip label={t('riskRegister.editAssessment')}>
                                                                <IconButton aria-label="Edit" icon={<EditIcon />} size="xs" colorScheme="blue" variant="ghost" onClick={() => openEditAssessModal(yr)} />
                                                              </Tooltip>
                                                            )}
                                                          </HStack>
                                                        </HStack>
                                                      </Box>

                                                      {/* Full Detail Panel */}
                                                      {isDetailOpen && detail && (
                                                        <Box px={4} py={3} bg="gray.50" borderTop="1px solid" borderColor="gray.200">
                                                          {/* Info Grid */}
                                                          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3} mb={4}>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('common.department')}</Text><Text fontWeight="bold" fontSize="sm">{detail.department ? (detail.department.work_unit ? `${detail.department.department_name} - ${detail.department.work_unit}` : detail.department.department_name) : detail.unit_id}</Text></Box>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('common.fiscalYear')}</Text><Text fontWeight="bold" fontSize="sm">{detail.fiscal_year?.year || '-'}</Text></Box>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('common.ermLevel')}</Text>
                                                              <Badge colorScheme={detail.erm_level === 'faculty' ? 'purple' : 'blue'}>{detail.erm_level === 'faculty' ? t('erm.faculty') : t('erm.department')}</Badge>
                                                            </Box>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('common.riskType')}</Text><Text fontWeight="bold" fontSize="sm">{detail.risk_type?.name_th || '-'}</Text></Box>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('common.strategy')}</Text><Text fontWeight="bold" fontSize="sm">{detail.strategy?.name || '-'}</Text></Box>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('common.riskCode')}</Text><Text fontWeight="bold" fontSize="sm">{detail.risk_code}</Text></Box>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('riskRegister.riskManagementType')}</Text><Text fontWeight="bold" fontSize="sm">{detail.risk_management_type?.name || '-'}</Text></Box>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('riskRegister.inherentRisk')}</Text>
                                                              <Badge bg={getRiskLevelStyle(detail.inherent_likelihood, detail.inherent_impact).bg} color={getRiskLevelStyle(detail.inherent_likelihood, detail.inherent_impact).text}>{getRiskLevelLabel(detail.inherent_likelihood, detail.inherent_impact)}</Badge>
                                                            </Box>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('riskRegister.inherentLikelihood')}</Text><Text fontWeight="bold" fontSize="sm">{detail.inherent_likelihood}</Text></Box>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('riskRegister.inherentImpact')}</Text><Text fontWeight="bold" fontSize="sm">{detail.inherent_impact}</Text></Box>
                                                            {detail.residual_risk != null && <Box><Text fontSize="xs" color="gray.500">{t('riskRegister.residualRisk')}</Text>
                                                              <Badge bg={getRiskLevelStyle(detail.residual_likelihood ?? 0, detail.residual_impact ?? 0).bg} color={getRiskLevelStyle(detail.residual_likelihood ?? 0, detail.residual_impact ?? 0).text}>{getRiskLevelLabel(detail.residual_likelihood ?? 0, detail.residual_impact ?? 0)}</Badge>
                                                            </Box>}
                                                            <Box><Text fontSize="xs" color="gray.500">{t('common.impactType')}</Text><Text fontWeight="bold" fontSize="sm">{detail.impact_type || '-'}</Text></Box>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('common.impactTo')}</Text><Text fontWeight="bold" fontSize="sm">{detail.impact_to || '-'}</Text></Box>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('common.assessmentResult')}</Text><Text fontWeight="bold" fontSize="sm">{detail.assessment_result || '-'}</Text></Box>
                                                            <Box><Text fontSize="xs" color="gray.500">{t('common.planPeriod')}</Text>
                                                              {detail.plan_start_month && detail.plan_end_month && detail.fiscal_year ? (() => {
                                                                const THAI_MONTHS = ['', 'มกราคม', 'กุมภาพันธ์', 'มีนาคม', 'เมษายน', 'พฤษภาคม', 'มิถุนายน', 'กรกฎาคม', 'สิงหาคม', 'กันยายน', 'ตุลาคม', 'พฤศจิกายน', 'ธันวาคม'];
                                                                const startYear = detail.plan_start_month! >= 10 ? detail.fiscal_year!.year - 1 : detail.fiscal_year!.year;
                                                                const endYear = detail.plan_end_month! <= 9 ? detail.fiscal_year!.year : detail.fiscal_year!.year + 1;
                                                                return <Text fontWeight="bold" fontSize="sm">{THAI_MONTHS[detail.plan_start_month!]} {startYear} ถึง {THAI_MONTHS[detail.plan_end_month!]} {endYear}</Text>;
                                                              })() : <Text fontWeight="bold" fontSize="sm">-</Text>}
                                                            </Box>
                                                            {detail.output && <Box gridColumn={{ md: 'span 2' }}><Text fontSize="xs" color="gray.500">{t('common.output')}</Text><Text fontWeight="bold" fontSize="sm">{detail.output}</Text></Box>}
                                                          </SimpleGrid>

                                                          {/* Causes */}
                                                          <Box mb={4}>
                                                            <HStack justifyContent="space-between" mb={2}>
                                                              <Text fontWeight="bold" fontSize="sm" color="orange.600">{t('section.causes', { n: detail.causes?.length || 0 })}</Text>
                                                              {!isGuest && (
                                                                <Button size="xs" leftIcon={<AddIcon />} colorScheme="orange" variant="outline"
                                                                  onClick={() => {
                                                                    setActiveRiskId(detail.risk_id);
                                                                    const maxNo = (detail.causes || []).reduce((max, c) => Math.max(max, c.cause_number), 0);
                                                                    setAddCauseData({ cause_id: '', cause_number: maxNo + 1, cause_description: '' });
                                                                    onAddCauseOpen();
                                                                  }}>{t('section.addCause')}</Button>
                                                              )}
                                                            </HStack>
                                                            {detail.causes && detail.causes.length > 0 ? (
                                                              <Accordion allowMultiple size="sm">
                                                                {detail.causes.map((cause) => (
                                                                  <AccordionItem key={cause.id} bg="white" borderRadius="md" mb={1} border="1px" borderColor="gray.200">
                                                                    <AccordionButton px={3} py={2}>
                                                                      <Box flex="1" textAlign="left">
                                                                        <HStack spacing={3}>
                                                                          <Badge colorScheme="orange">{cause.cause_id}</Badge>
                                                                          <Text fontSize="sm" fontWeight="bold">{t('section.cause')} {cause.cause_number}</Text>
                                                                        </HStack>
                                                                        <Text fontSize="xs" color="gray.600" mt={1}>{cause.cause_description}</Text>
                                                                      </Box>
                                                                      <HStack spacing={1} mr={2}>
                                                                        {!isGuest && <Tooltip label={t('riskRegister.edit')}><IconButton aria-label="Edit" icon={<EditIcon />} size="xs" colorScheme="yellow" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditCause(cause); }} /></Tooltip>}
                                                                        {!isGuest && <Tooltip label={t('riskRegister.delete')}><IconButton aria-label="Delete" icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteCause(cause.id); }} /></Tooltip>}
                                                                      </HStack>
                                                                      <AccordionIcon />
                                                                    </AccordionButton>
                                                                    <AccordionPanel px={3} pb={3}>
                                                                      <Text fontSize="xs">{cause.cause_description}</Text>
                                                                    </AccordionPanel>
                                                                  </AccordionItem>
                                                                ))}
                                                              </Accordion>
                                                            ) : (
                                                              <Text fontSize="xs" color="gray.400" fontStyle="italic">{t('section.noCauses')}</Text>
                                                            )}
                                                          </Box>

                                                          {/* Internal Controls */}
                                                          <Box mb={4}>
                                                            <HStack justifyContent="space-between" mb={2}>
                                                              <Text fontWeight="bold" fontSize="sm" color="green.600">{t('section.internalControls', { n: detail.internal_controls?.length || 0 })}</Text>
                                                              {!isGuest && <Button size="xs" leftIcon={<AddIcon />} colorScheme="green" variant="outline" onClick={() => openControlModal(detail, 'IC')}>{t('section.addIc')}</Button>}
                                                            </HStack>
                                                            {detail.internal_controls && detail.internal_controls.length > 0 ? (
                                                              <Accordion allowMultiple size="sm">
                                                                {detail.internal_controls.map((ic) => (
                                                                  <AccordionItem key={ic.id} bg="white" borderRadius="md" mb={1} border="1px" borderColor="green.200">
                                                                    <AccordionButton px={3} py={2}>
                                                                      <Box flex="1" textAlign="left">
                                                                        <HStack spacing={2}>
                                                                          <Badge colorScheme="green">{ic.ic_id}</Badge>
                                                                          <Text fontSize="sm" fontWeight="bold">IC{ic.ic_no}</Text>
                                                                          <Badge colorScheme="teal" fontSize="xs">{ic.control_type?.name || '-'}</Badge>
                                                                        </HStack>
                                                                        <Text fontSize="xs" color="gray.600" mt={1}>{ic.control_description}</Text>
                                                                      </Box>
                                                                      <HStack spacing={1} mr={2}>
                                                                        {!isGuest && <Tooltip label={t('riskRegister.edit')}><IconButton aria-label="Edit" icon={<EditIcon />} size="xs" colorScheme="yellow" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditIC(ic); }} /></Tooltip>}
                                                                        {!isGuest && <Tooltip label={t('riskRegister.delete')}><IconButton aria-label="Delete" icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteIC(ic.id); }} /></Tooltip>}
                                                                      </HStack>
                                                                      <AccordionIcon />
                                                                    </AccordionButton>
                                                                    <AccordionPanel px={3} pb={3}>
                                                                      <Text fontSize="xs">{ic.control_description}</Text>
                                                                      <Text fontSize="xs" color="gray.500" mt={1}>{t('common.owner')}: {ic.ic_owner || '-'}</Text>
                                                                      {ic.evidence_link && (
                                                                        <Box mt={2} p={2} bg="green.50" borderRadius="md" border="1px solid" borderColor="green.200">
                                                                          <a href={ic.evidence_link.startsWith('http') ? ic.evidence_link : `${API_BASE_URL}${ic.evidence_link}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#2B6CB0', textDecoration: 'underline' }}>
                                                                            <ViewIcon boxSize={3} /> {ic.evidence_name || ic.evidence_link}
                                                                          </a>
                                                                        </Box>
                                                                      )}
                                                                    </AccordionPanel>
                                                                  </AccordionItem>
                                                                ))}
                                                              </Accordion>
                                                            ) : (
                                                              <Text fontSize="xs" color="gray.400" fontStyle="italic">{t('section.noIcs')}</Text>
                                                            )}
                                                          </Box>

                                                          {/* Additional Controls */}
                                                          <Box mb={4}>
                                                            <HStack justifyContent="space-between" mb={2}>
                                                              <Text fontWeight="bold" fontSize="sm" color="yellow.600">{t('section.additionalControls', { n: detail.additional_controls?.length || 0 })}</Text>
                                                              {!isGuest && <Button size="xs" leftIcon={<AddIcon />} colorScheme="yellow" variant="outline" onClick={() => openControlModal(detail, 'AC')}>{t('section.addAc')}</Button>}
                                                            </HStack>
                                                            {detail.additional_controls && detail.additional_controls.length > 0 ? (
                                                              <Accordion allowMultiple size="sm">
                                                                {detail.additional_controls.map((ac) => (
                                                                  <AccordionItem key={ac.id} bg="white" borderRadius="md" mb={1} border="1px" borderColor="yellow.200">
                                                                    <AccordionButton px={3} py={2}>
                                                                      <Box flex="1" textAlign="left">
                                                                        <HStack spacing={2}>
                                                                          <Badge colorScheme="yellow">{ac.ac_id}</Badge>
                                                                          <Text fontSize="sm" fontWeight="bold">AC{ac.ac_no}</Text>
                                                                          <Badge colorScheme="orange" fontSize="xs">{ac.control_type?.name || '-'}</Badge>
                                                                        </HStack>
                                                                        <Text fontSize="xs" color="gray.600" mt={1}>{ac.control_description}</Text>
                                                                      </Box>
                                                                      <HStack spacing={1} mr={2}>
                                                                        {!isGuest && <Tooltip label={t('riskRegister.edit')}><IconButton aria-label="Edit" icon={<EditIcon />} size="xs" colorScheme="yellow" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditAC(ac); }} /></Tooltip>}
                                                                        {!isGuest && <Tooltip label={t('riskRegister.delete')}><IconButton aria-label="Delete" icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteAC(ac.id); }} /></Tooltip>}
                                                                      </HStack>
                                                                      <AccordionIcon />
                                                                    </AccordionButton>
                                                                    <AccordionPanel px={3} pb={3}>
                                                                      <Text fontSize="xs">{ac.control_description}</Text>
                                                                      <Text fontSize="xs" color="gray.500" mt={1}>{t('common.owner')}: {ac.ac_owner || '-'}</Text>
                                                                      {ac.evidence_link && (
                                                                        <Box mt={2} p={2} bg="yellow.50" borderRadius="md" border="1px solid" borderColor="yellow.200">
                                                                          <a href={ac.evidence_link.startsWith('http') ? ac.evidence_link : `${API_BASE_URL}${ac.evidence_link}`} target="_blank" rel="noopener noreferrer" style={{ fontSize: '12px', color: '#2B6CB0', textDecoration: 'underline' }}>
                                                                            <ViewIcon boxSize={3} /> {ac.evidence_name || ac.evidence_link}
                                                                          </a>
                                                                        </Box>
                                                                      )}
                                                                    </AccordionPanel>
                                                                  </AccordionItem>
                                                                ))}
                                                              </Accordion>
                                                            ) : (
                                                              <Text fontSize="xs" color="gray.400" fontStyle="italic">{t('section.noAcs')}</Text>
                                                            )}
                                                          </Box>

                                                          {/* KRI */}
                                                          <Box mb={2}>
                                                            <HStack justifyContent="space-between" mb={2}>
                                                              <Text fontWeight="bold" fontSize="sm" color="purple.600">{t('section.kri', { n: detail.kri_records?.length || 0 })}</Text>
                                                              {!isGuest && <Button size="xs" leftIcon={<AddIcon />} colorScheme="purple" variant="outline" onClick={() => openControlModal(detail, 'KRI')}>{t('section.addKri')}</Button>}
                                                            </HStack>
                                                            {detail.kri_records && detail.kri_records.length > 0 ? (
                                                              <Accordion allowMultiple size="sm">
                                                                {detail.kri_records.map((kri) => (
                                                                  <AccordionItem key={kri.id} bg="white" borderRadius="md" mb={1} border="1px" borderColor="purple.200">
                                                                    <AccordionButton px={3} py={2}>
                                                                      <Box flex="1" textAlign="left">
                                                                        <HStack spacing={2}>
                                                                          <Badge colorScheme="purple">{kri.kri_id}</Badge>
                                                                          <Text fontSize="sm" fontWeight="bold">{kri.kri_name}</Text>
                                                                        </HStack>
                                                                         <Text fontSize="xs" color="gray.600" mt={1}>{t('section.target')}: {kri.target_value || '-'} {kri.unit || ''} | {t('common.owner')}: {kri.kri_owner || '-'} | {t('section.result')}: {kri.actual_result || '-'} | {t('common.measurementDate')}: {kri.measurement_date || '-'}</Text>
                                                                      </Box>
                                                                       <HStack spacing={1} mr={2}>
                                                                         {!isGuest && <Tooltip label={t('riskRegister.report')}><IconButton aria-label="Report" icon={<ViewIcon />} size="xs" colorScheme="teal" variant="ghost" onClick={(e) => { e.stopPropagation(); openReportModal(kri, detail.risk_id, detail.output || ''); }} /></Tooltip>}
                                                                         {!isGuest && <Tooltip label={t('riskRegister.edit')}><IconButton aria-label="Edit" icon={<EditIcon />} size="xs" colorScheme="yellow" variant="ghost" onClick={(e) => { e.stopPropagation(); openEditKRI(kri); }} /></Tooltip>}
                                                                         {!isGuest && <Tooltip label={t('riskRegister.delete')}><IconButton aria-label="Delete" icon={<DeleteIcon />} size="xs" colorScheme="red" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeleteKRI(kri.id); }} /></Tooltip>}
                                                                       </HStack>
                                                                       <AccordionIcon />
                                                                     </AccordionButton>
                                                                     <AccordionPanel px={3} pb={3}>
                                                                       <Text fontSize="xs">{t('section.target')}: {kri.target_value || '-'} {kri.unit || ''}</Text>
                                                                       <Text fontSize="xs" color="gray.500" mt={1}>{t('common.owner')}: {kri.kri_owner || '-'}</Text>
                                                                       <Text fontSize="xs" color="gray.500">{t('section.result')}: {kri.actual_result || '-'}</Text>
                                                                       <Text fontSize="xs" color="gray.500">{t('common.measurementDate')}: {kri.measurement_date || '-'}</Text>
                                                                     </AccordionPanel>
                                                                    </AccordionItem>
                                                                  ))}
                                                              </Accordion>
                                                            ) : (
                                                              <Text fontSize="xs" color="gray.400" fontStyle="italic">{t('section.noKri')}</Text>
                                                            )}
                                                          </Box>
                                                        </Box>
                                                      )}
                                                    </Box>
                                                  );
                                                })}
                                              </Box>
                                            </Box>
                                          );
                                        })}
                                      </Box>
                                    );
                                  });
                                })()}
                              </Box>
                            </Td>
                          </Tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </Tbody>
              </Table>
              </Box>
            </Box>
          ));
        })()}
      </VStack>

      {/* Add Cause Modal */}
      <Modal isOpen={isAddCauseOpen} onClose={onAddCauseClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('modal.addCause')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl><FormLabel>{t('modal.causeId')}</FormLabel><Input value={addCauseData.cause_id} onChange={(e) => setAddCauseData({ ...addCauseData, cause_id: e.target.value })} placeholder={t('modal.autoHint')} /></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.number')}</FormLabel><NumberInput min={1} value={addCauseData.cause_number} onChange={(v) => setAddCauseData({ ...addCauseData, cause_number: Number(v) })}><NumberInputField /></NumberInput></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.causeDescription')}</FormLabel><Textarea value={addCauseData.cause_description} onChange={(e) => setAddCauseData({ ...addCauseData, cause_description: e.target.value })} rows={3} /></FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAddCauseClose}>{t('common.cancel')}</Button>
            <Button colorScheme="orange" onClick={handleAddCause}>{t('common.add')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add IC Modal */}
      <Modal isOpen={isAddICOpen} onClose={onAddICClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('modal.addIc')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="info" borderRadius="md" size="sm">
                <AlertIcon />
                <Text fontSize="xs">{t('modal.autoHint')}</Text>
              </Alert>
              <FormControl isRequired><FormLabel>{t('modal.icNo')}</FormLabel><NumberInput min={1} value={addICData.ic_no} onChange={(v) => setAddICData({ ...addICData, ic_no: Number(v) })}><NumberInputField /></NumberInput></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.description')}</FormLabel><Textarea value={addICData.control_description} onChange={(e) => setAddICData({ ...addICData, control_description: e.target.value })} rows={3} /></FormControl>
              <FormControl><FormLabel>{t('modal.icOwner')}</FormLabel><Input value={addICData.ic_owner} onChange={(e) => setAddICData({ ...addICData, ic_owner: e.target.value })} /></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.controlType')}</FormLabel>
                <Select value={addICData.control_type_id} onChange={(e) => setAddICData({ ...addICData, control_type_id: Number(e.target.value) })}>
                  {controlTypes.map((ct) => (<option key={ct.id} value={ct.id}>{ct.name}</option>))}
                </Select>
              </FormControl>
              <FormControl><FormLabel>{t('modal.evidence')}</FormLabel><EvidenceUpload value={addICData.evidence_link} fileName={addICData.evidence_name} fiscalYearId={activeFiscalYearId} onChange={(link, name) => setAddICData({ ...addICData, evidence_link: link, evidence_name: name })} /></FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAddICClose}>{t('common.cancel')}</Button>
            <Button colorScheme="green" onClick={handleAddIC}>{t('common.add')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add AC Modal */}
      <Modal isOpen={isAddACOpen} onClose={onAddACClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('modal.addAc')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="info" borderRadius="md" size="sm">
                <AlertIcon />
                <Text fontSize="xs">{t('modal.autoHint')}</Text>
              </Alert>
              <FormControl isRequired><FormLabel>{t('modal.acNo')}</FormLabel><NumberInput min={1} value={addACData.ac_no} onChange={(v) => setAddACData({ ...addACData, ac_no: Number(v) })}><NumberInputField /></NumberInput></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.description')}</FormLabel><Textarea value={addACData.control_description} onChange={(e) => setAddACData({ ...addACData, control_description: e.target.value })} rows={3} /></FormControl>
              <FormControl><FormLabel>{t('modal.acOwner')}</FormLabel><Input value={addACData.ac_owner} onChange={(e) => setAddACData({ ...addACData, ac_owner: e.target.value })} /></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.controlType')}</FormLabel>
                <Select value={addACData.control_type_id} onChange={(e) => setAddACData({ ...addACData, control_type_id: Number(e.target.value) })}>
                  {controlTypes.map((ct) => (<option key={ct.id} value={ct.id}>{ct.name}</option>))}
                </Select>
              </FormControl>
              <FormControl><FormLabel>{t('modal.evidence')}</FormLabel><EvidenceUpload value={addACData.evidence_link} fileName={addACData.evidence_name} fiscalYearId={activeFiscalYearId} onChange={(link, name) => setAddACData({ ...addACData, evidence_link: link, evidence_name: name })} /></FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAddACClose}>{t('common.cancel')}</Button>
            <Button colorScheme="yellow" onClick={handleAddAC}>{t('common.add')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Add KRI Modal */}
      <Modal isOpen={isAddKRIOpen} onClose={onAddKRIClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('modal.kriForm')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <Alert status="info" borderRadius="md" size="sm">
                <AlertIcon />
                <Text fontSize="xs">{t('modal.autoHint')}</Text>
              </Alert>
              <FormControl isRequired><FormLabel>{t('modal.kriName')}</FormLabel><Input value={addKRIData.kri_name} onChange={(e) => setAddKRIData({ ...addKRIData, kri_name: e.target.value })} /></FormControl>
              <FormControl><FormLabel>{t('modal.targetValue')}</FormLabel><Input value={addKRIData.target_value} onChange={(e) => setAddKRIData({ ...addKRIData, target_value: e.target.value })} /></FormControl>
              <FormControl><FormLabel>{t('modal.unit')}</FormLabel><Input value={addKRIData.unit} onChange={(e) => setAddKRIData({ ...addKRIData, unit: e.target.value })} /></FormControl>
              <FormControl><FormLabel>{t('modal.kriOwner')}</FormLabel><Input value={addKRIData.kri_owner} onChange={(e) => setAddKRIData({ ...addKRIData, kri_owner: e.target.value })} /></FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAddKRIClose}>{t('common.cancel')}</Button>
            <Button colorScheme="purple" onClick={handleAddKRI}>{t('common.add')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit Cause Modal */}
      <Modal isOpen={isEditCauseOpen} onClose={onEditCauseClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('modal.editCause')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired><FormLabel>{t('modal.causeId')}</FormLabel><Input value={editCauseData.cause_id} onChange={(e) => setEditCauseData({ ...editCauseData, cause_id: e.target.value })} /></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.number')}</FormLabel><NumberInput min={1} value={editCauseData.cause_number} onChange={(v) => setEditCauseData({ ...editCauseData, cause_number: Number(v) })}><NumberInputField /></NumberInput></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.causeDescription')}</FormLabel><Textarea value={editCauseData.cause_description} onChange={(e) => setEditCauseData({ ...editCauseData, cause_description: e.target.value })} rows={3} /></FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditCauseClose}>{t('common.cancel')}</Button>
            <Button colorScheme="orange" onClick={handleEditCause}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit IC Modal */}
      <Modal isOpen={isEditICOpen} onClose={onEditICClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('modal.editIc')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired><FormLabel>{t('modal.icId')}</FormLabel><Input value={editICData.ic_id} isDisabled /></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.icNo')}</FormLabel><NumberInput min={1} value={editICData.ic_no} onChange={(v) => setEditICData({ ...editICData, ic_no: Number(v) })}><NumberInputField /></NumberInput></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.description')}</FormLabel><Textarea value={editICData.control_description} onChange={(e) => setEditICData({ ...editICData, control_description: e.target.value })} rows={3} /></FormControl>
              <FormControl><FormLabel>{t('modal.icOwner')}</FormLabel><Input value={editICData.ic_owner} onChange={(e) => setEditICData({ ...editICData, ic_owner: e.target.value })} /></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.controlType')}</FormLabel>
                <Select value={editICData.control_type_id} onChange={(e) => setEditICData({ ...editICData, control_type_id: Number(e.target.value) })}>
                  {controlTypes.map((ct) => (<option key={ct.id} value={ct.id}>{ct.name}</option>))}
                </Select>
              </FormControl>
              <FormControl><FormLabel>{t('modal.evidence')}</FormLabel><EvidenceUpload value={editICData.evidence_link} fileName={editICData.evidence_name} fiscalYearId={activeFiscalYearId} onChange={(link, name) => setEditICData({ ...editICData, evidence_link: link, evidence_name: name })} /></FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditICClose}>{t('common.cancel')}</Button>
            <Button colorScheme="green" onClick={handleEditIC}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit AC Modal */}
      <Modal isOpen={isEditACOpen} onClose={onEditACClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('modal.editAc')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired><FormLabel>{t('modal.acId')}</FormLabel><Input value={editACData.ac_id} isDisabled /></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.acNo')}</FormLabel><NumberInput min={1} value={editACData.ac_no} onChange={(v) => setEditACData({ ...editACData, ac_no: Number(v) })}><NumberInputField /></NumberInput></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.description')}</FormLabel><Textarea value={editACData.control_description} onChange={(e) => setEditACData({ ...editACData, control_description: e.target.value })} rows={3} /></FormControl>
              <FormControl><FormLabel>{t('modal.acOwner')}</FormLabel><Input value={editACData.ac_owner} onChange={(e) => setEditACData({ ...editACData, ac_owner: e.target.value })} /></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.controlType')}</FormLabel>
                <Select value={editACData.control_type_id} onChange={(e) => setEditACData({ ...editACData, control_type_id: Number(e.target.value) })}>
                  {controlTypes.map((ct) => (<option key={ct.id} value={ct.id}>{ct.name}</option>))}
                </Select>
              </FormControl>
              <FormControl><FormLabel>{t('modal.evidence')}</FormLabel><EvidenceUpload value={editACData.evidence_link} fileName={editACData.evidence_name} fiscalYearId={activeFiscalYearId} onChange={(link, name) => setEditACData({ ...editACData, evidence_link: link, evidence_name: name })} /></FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditACClose}>{t('common.cancel')}</Button>
            <Button colorScheme="yellow" onClick={handleEditAC}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Edit KRI Modal */}
      <Modal isOpen={isEditKRIOpen} onClose={onEditKRIClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('modal.kriForm')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired><FormLabel>{t('modal.kriId')}</FormLabel><Input value={editKRIData.kri_id} isDisabled /></FormControl>
              <FormControl isRequired><FormLabel>{t('modal.kriName')}</FormLabel><Input value={editKRIData.kri_name} onChange={(e) => setEditKRIData({ ...editKRIData, kri_name: e.target.value })} /></FormControl>
              <FormControl><FormLabel>{t('modal.targetValue')}</FormLabel><Input value={editKRIData.target_value} onChange={(e) => setEditKRIData({ ...editKRIData, target_value: e.target.value })} /></FormControl>
              <FormControl><FormLabel>{t('modal.unit')}</FormLabel><Input value={editKRIData.unit} onChange={(e) => setEditKRIData({ ...editKRIData, unit: e.target.value })} /></FormControl>
              <FormControl><FormLabel>{t('modal.kriOwner')}</FormLabel><Input value={editKRIData.kri_owner} onChange={(e) => setEditKRIData({ ...editKRIData, kri_owner: e.target.value })} /></FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onEditKRIClose}>{t('common.cancel')}</Button>
            <Button colorScheme="purple" onClick={handleEditKRI}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Report Result Modal (รายงานผลดำเนินการ) */}
      <Modal isOpen={isReportOpen} onClose={onReportClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="teal.600">{t('modal.reportResult')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box p={3} bg="purple.50" borderRadius="md">
                <Text fontWeight="bold" fontSize="sm" color="purple.700" mb={2}>{t('modal.kriInfo')}</Text>
                <SimpleGrid columns={2} spacing={2}>
                  <Box><Text fontSize="xs" color="gray.500">{t('modal.kriName')}</Text><Text fontWeight="bold" fontSize="sm">{reportData.kri_name}</Text></Box>
                  <Box><Text fontSize="xs" color="gray.500">{t('modal.targetValue')} / {t('modal.unit')}</Text><Text fontWeight="bold" fontSize="sm">{reportData.target_value || '-'} {reportData.unit || ''}</Text></Box>
                </SimpleGrid>
              </Box>

              <FormControl isRequired>
                <FormLabel>{t('modal.actualResult')}</FormLabel>
                <Input value={reportData.actual_result} onChange={(e) => setReportData({ ...reportData, actual_result: e.target.value })} placeholder={t('modal.actualResultPlaceholder')} />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>{t('modal.measurementDate')}</FormLabel>
                <Input type="date" value={reportData.measurement_date} onChange={(e) => setReportData({ ...reportData, measurement_date: e.target.value })} />
              </FormControl>

              <Box p={3} bg="gray.50" borderRadius="md">
                <Text fontWeight="bold" fontSize="sm" color="gray.700" mb={2}>{t('modal.outputSection')}</Text>
                <FormControl isRequired>
                  <FormLabel>{t('modal.output')}</FormLabel>
                  <Textarea value={reportData.output} onChange={(e) => setReportData({ ...reportData, output: e.target.value })} placeholder={t('modal.outputPlaceholder')} rows={3} />
                </FormControl>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onReportClose}>{t('common.cancel')}</Button>
            <Button colorScheme="teal" onClick={handleReportSave}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Copy to Next Year Modal */}
      <Modal isOpen={isCopyOpen} onClose={onCopyClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="teal.600">{t('copy.title')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box bg="gray.50" p={4} borderRadius="md">
                <Text fontSize="sm"><strong>{t('copy.risk')}:</strong> {copyTarget?.risk_code} — {copyTarget?.risk_name}</Text>
                <Text fontSize="sm" mt={1}><strong>{t('common.department')}:</strong> {copyTarget?.department ? (copyTarget.department.work_unit ? `${copyTarget.department.department_name} - ${copyTarget.department.work_unit}` : copyTarget.department.department_name) : copyTarget?.unit_id}</Text>
                <Text fontSize="sm" mt={1}><strong>{t('copy.currentYear')}:</strong> {copyTarget?.fiscal_year?.year || '-'}</Text>
              </Box>
              <Box bg="teal.50" p={4} borderRadius="md">
                <Text fontSize="sm" fontWeight="bold" color="teal.700">
                  {t('copy.nextYear')}: {fiscalYears.find((fy) => fy.id === copyYearId)?.year || '-'}
                </Text>
              </Box>
              <Text fontSize="xs" color="gray.500">
                {t('copy.description')}
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onCopyClose}>{t('common.cancel')}</Button>
            <Button colorScheme="teal" onClick={handleCopy} isLoading={copying} leftIcon={<CopyIcon />}>
              {t('copy.andEdit')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Batch Copy to New Year Modal */}
      <Modal isOpen={isBatchCopyOpen} onClose={batchCopying ? () => {} : onBatchCopyClose} size="md" isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader color="teal.600">{t('copy.titleBatch')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4} align="stretch">
              <Box bg="gray.50" p={4} borderRadius="md" maxH="200px" overflowY="auto">
                <Text fontSize="sm" fontWeight="bold" mb={2}>{t('copy.selected', { n: selectedRisks.length })}</Text>
                <VStack spacing={1} align="stretch">
                  {selectedRisks.map((r) => (
                    <HStack key={r.risk_id} justifyContent="space-between">
                      <Checkbox isChecked onChange={() => toggleSelect(r.risk_id)} aria-label={`Remove ${r.risk_code}`}>
                        <Text fontSize="sm">{r.risk_code} — {r.risk_name}</Text>
                      </Checkbox>
                      <Badge fontSize="xs" colorScheme="gray">{r.fiscal_year?.year || '-'}</Badge>
                    </HStack>
                  ))}
                </VStack>
              </Box>
              <Text fontSize="xs" color="gray.500">
                {t('copy.descriptionBatch')}
              </Text>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onBatchCopyClose}>{t('common.cancel')}</Button>
            <Button colorScheme="teal" onClick={handleBatchCopy} isLoading={batchCopying} loadingText={t('copy.copying')}
              leftIcon={<CopyIcon />} isDisabled={selectedRisks.length === 0 || batchCopying}>
              {t('copy.copyCount', { n: selectedRisks.length, n2: selectedRisks.length === 1 ? '' : 's' })}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Risk Master Modal (Add/Edit) */}
      <Modal isOpen={isMasterModalOpen} onClose={onMasterModalClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingMasterCode ? t('riskRegister.editMasterRisk') : t('riskRegister.addMasterRisk')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>{t('common.riskCode')}</FormLabel>
                <Input value={masterModalData.risk_code} isDisabled />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('common.riskName')}</FormLabel>
                <Textarea value={masterModalData.risk_name} onChange={(e) => setMasterModalData({ ...masterModalData, risk_name: e.target.value })} rows={2} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('common.type')}</FormLabel>
                <Select value={masterModalData.risk_type_id} onChange={(e) => setMasterModalData({ ...masterModalData, risk_type_id: Number(e.target.value) })}>
                  <option value={0}>{t('form.selectRiskType')}</option>
                  {riskTypes.map((rt) => (<option key={rt.id} value={rt.id}>{rt.name_th}</option>))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('common.strategy')}</FormLabel>
                <Select value={masterModalData.strategy_id} onChange={(e) => setMasterModalData({ ...masterModalData, strategy_id: Number(e.target.value) })}>
                  <option value={0}>{t('form.selectStrategy')}</option>
                  {strategies.map((s) => (<option key={s.id} value={s.id}>{s.name}</option>))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            {editingMasterCode && !isGuest && (
              <Button colorScheme="red" variant="ghost" mr="auto" onClick={() => { onMasterModalClose(); handleDeleteMaster(editingMasterCode); }}>{t('common.delete')}</Button>
            )}
            <Button variant="ghost" mr={3} onClick={onMasterModalClose}>{t('common.cancel')}</Button>
            <Button colorScheme="green" onClick={handleSaveMaster}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Assessment Modal (Add/Edit risk-year-department) */}
      <Modal isOpen={isAssessModalOpen} onClose={onAssessModalClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingAssessRiskId ? t('riskRegister.editAssessment') : t('riskRegister.addAssessment')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <SimpleGrid columns={2} spacing={4}>
              <FormControl isRequired>
                <FormLabel>{t('common.riskCode')}</FormLabel>
                <Input value={assessModalData.risk_code} isDisabled />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('form.riskId')}</FormLabel>
                <Input value={assessModalData.risk_id} isDisabled />
              </FormControl>
              <FormControl isRequired gridColumn={{ md: 'span 2' }}>
                <FormLabel>{t('common.riskName')}</FormLabel>
                <Textarea value={assessModalData.risk_name} onChange={(e) => setAssessModalData({ ...assessModalData, risk_name: e.target.value })} rows={2} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('common.fiscalYear')}</FormLabel>
                <Select value={assessModalData.fiscal_year_id} onChange={(e) => setAssessModalData({ ...assessModalData, fiscal_year_id: Number(e.target.value) })} isDisabled={!!editingAssessRiskId}>
                  <option value={0}>{t('form.selectFiscalYear')}</option>
                  {fiscalYears.map((fy) => (<option key={fy.id} value={fy.id}>{fy.year}</option>))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('form.department')}</FormLabel>
                <Select value={assessModalData.unit_id} onChange={(e) => setAssessModalData({ ...assessModalData, unit_id: e.target.value })} isDisabled={!isAdmin || !!editingAssessRiskId}>
                  <option value="">{t('form.selectDepartment')}</option>
                  {departments.map((d) => (<option key={d.unit_id} value={d.unit_id}>{d.work_unit ? `${d.department_name} - ${d.work_unit}` : d.department_name}</option>))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('form.riskManagementType')}</FormLabel>
                <Select value={assessModalData.risk_management_type_id} onChange={(e) => setAssessModalData({ ...assessModalData, risk_management_type_id: Number(e.target.value) })}>
                  <option value={0}>{t('form.selectRmt')}</option>
                  {riskMgmtTypes.map((rmt) => (<option key={rmt.id} value={rmt.id}>{rmt.name}</option>))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t('form.riskOwner')}</FormLabel>
                <Input value={assessModalData.risk_owner} onChange={(e) => setAssessModalData({ ...assessModalData, risk_owner: e.target.value })} />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('riskRegister.inherentLikelihood')}</FormLabel>
                <Select value={assessModalData.inherent_likelihood} onChange={(e) => setAssessModalData({ ...assessModalData, inherent_likelihood: Number(e.target.value) })}>
                  {[1,2,3,4,5].map((n) => (<option key={n} value={n}>{n}</option>))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>{t('riskRegister.inherentImpact')}</FormLabel>
                <Select value={assessModalData.inherent_impact} onChange={(e) => setAssessModalData({ ...assessModalData, inherent_impact: Number(e.target.value) })}>
                  {[1,2,3,4,5].map((n) => (<option key={n} value={n}>{n}</option>))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t('riskRegister.residualLikelihood')}</FormLabel>
                <Select value={assessModalData.residual_likelihood || ''} onChange={(e) => setAssessModalData({ ...assessModalData, residual_likelihood: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">-</option>
                  {[1,2,3,4,5].map((n) => (<option key={n} value={n}>{n}</option>))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t('riskRegister.residualImpact')}</FormLabel>
                <Select value={assessModalData.residual_impact || ''} onChange={(e) => setAssessModalData({ ...assessModalData, residual_impact: e.target.value ? Number(e.target.value) : null })}>
                  <option value="">-</option>
                  {[1,2,3,4,5].map((n) => (<option key={n} value={n}>{n}</option>))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t('form.ermLevel')}</FormLabel>
                <Select value={assessModalData.erm_level} onChange={(e) => setAssessModalData({ ...assessModalData, erm_level: e.target.value })}>
                  <option value="department">{t('erm.dept')}</option>
                  <option value="faculty">{t('erm.erm')}</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t('common.impactType')}</FormLabel>
                <Select value={assessModalData.impact_type} onChange={(e) => setAssessModalData({ ...assessModalData, impact_type: e.target.value })} placeholder={t('form.selectOption') || 'เลือก'}>
                  <option value="ด้านประสิทธิผล">ด้านประสิทธิผล</option>
                  <option value="ด้านมูลค่าความเสียหายทางการเงิน">ด้านมูลค่าความเสียหายทางการเงิน</option>
                  <option value="ด้านชื่อเสียงและภาพลักษณ์">ด้านชื่อเสียงและภาพลักษณ์</option>
                  <option value="ด้านความปลอดภัย">ด้านความปลอดภัย</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t('common.impactTo')}</FormLabel>
                <Select value={assessModalData.impact_to} onChange={(e) => setAssessModalData({ ...assessModalData, impact_to: e.target.value })} placeholder={t('form.selectOption') || 'เลือก'}>
                  <option value="ส่วนงาน">ส่วนงาน</option>
                  <option value="มหาวิทยาลัย">มหาวิทยาลัย</option>
                  <option value="ส่วนงานและมหาวิทยาลัย">ส่วนงานและมหาวิทยาลัย</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t('common.assessmentResult')}</FormLabel>
                <Input value={assessModalData.assessment_result} onChange={(e) => setAssessModalData({ ...assessModalData, assessment_result: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel>{t('common.planPeriod')}</FormLabel>
                <HStack>
                  <Select placeholder="เริ่ม" value={assessModalData.plan_start_month || ''} onChange={(e) => setAssessModalData({ ...assessModalData, plan_start_month: e.target.value ? Number(e.target.value) : null })} size="sm">
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (<option key={m} value={m}>{m}</option>))}
                  </Select>
                  <Text>-</Text>
                  <Select placeholder="สิ้นสุด" value={assessModalData.plan_end_month || ''} onChange={(e) => setAssessModalData({ ...assessModalData, plan_end_month: e.target.value ? Number(e.target.value) : null })} size="sm">
                    {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => (<option key={m} value={m}>{m}</option>))}
                  </Select>
                </HStack>
              </FormControl>
              <FormControl gridColumn={{ md: 'span 2' }}>
                <FormLabel>{t('common.output')}</FormLabel>
                <Textarea value={assessModalData.output} onChange={(e) => setAssessModalData({ ...assessModalData, output: e.target.value })} rows={2} />
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAssessModalClose}>{t('common.cancel')}</Button>
            <Button colorScheme="green" onClick={handleSaveAssess}>{t('common.save')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default RiskRegisterPage;
